import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useApp, LANGUAGES } from "@/context/AppContext";
import { useSeo } from "@/lib/seo";
import { YalolaLogo } from "@/components/YalolaLogo";
import { api } from "@/lib/api";
import { countryList, DIAL_CODES } from "@/lib/countries";
import { detectClientCountry } from "@/lib/geoDetect";
import { CityAutocomplete } from "@/components/CityAutocomplete";
import { NearestCity } from "@/components/NearestCity";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { BirthDatePicker } from "@/components/BirthDatePicker";
import { PasswordInput } from "@/components/ui/password-input";
import { PasswordStrength } from "@/components/ui/password-strength";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PhoneField, flagEmoji } from "@/components/PhoneField";
import { IdentifierField } from "@/components/auth/IdentifierField";
import { detectIdentifierKind, isValidEmail, phoneFromInput, loadRememberedIdentifier, saveRememberedIdentifier, forgetRememberedIdentifier } from "@/lib/identifier";
import { rememberLoginMethod, recallLoginMethod, orderLoginMethods, forgetLoginMethods } from "@/lib/loginMethodMemory";
import { EMPTY_CHECK, checkPhone, isBlockingCheck, phoneIssueMessage } from "@/lib/phoneCheck";
import { loadRegisterDraft, saveRegisterDraft, clearRegisterDraft } from "@/lib/registerDraft";
import { toast } from "sonner";
import { Church, HeartHandshake, Radio, Users, ChevronLeft, ChevronRight, Star, MailCheck, KeyRound, Mail, MessageCircle, ShieldCheck, ShieldAlert, Info, UserRound, UserPlus, X } from "lucide-react";
import { Flag } from "@/components/Flag";
import { LangFlags, withLang } from "@/components/LangFlags";
import { InstallAppButton } from "@/components/InstallAppButton";
import { StatusBarShim } from "@/components/StatusBarShim";
import { NavBarBackdrop } from "@/components/NavBarBackdrop";
import { WelcomeCountryBanner } from "@/components/WelcomeCountryBanner"; // lot 767
import { nativeConsentFlow } from "@/lib/welcomeConsent";
import { AGE_BLOCK_EVENT, ageAssurancePayload, checkAgeRange, isBlocking, isIosNative, readAgeRange, setAgeBlock } from "@/lib/ageRange";

import { interestOptions } from "@/lib/interests";
import { storedRefCode, storeRefCode, clearRefCode, normalizeRefCode } from "@/lib/referral"; // lot 705 — code d'invitation

const FALLBACK_DENOMS = [
  "Évangélique", "Pentecôtiste", "Baptiste", "Catholique", "Orthodoxe",
  "Méthodiste", "Presbytérienne", "Réveil", "Non-dénominationnelle", "Autre",
];

// Full page translations — every text on this page follows the chosen language.
const L10N = {
  fr: {
    subtitle: "Le réseau chrétien qui rassemble.",
    desc: "Partagez vos versets, priez ensemble, trouvez une église, rencontrez des frères et sœurs qui partagent votre foi.",
    chip_church: "Trouve une église", chip_match: "Points en commun", chip_live: "Live & offrandes", chip_groups: "Groupes de foi",
    choose_lang: "Choisis ta langue",
    login: "Se connecter", register: "Créer un compte",
    email: "E-mail", password: "Mot de passe", password_hint: "(6+ car.)",
    logging_in: "Connexion...", creating: "Création...",
    demo: "",
    first_name: "Prénom", last_name: "Nom", intro: "Intro (présente-toi en quelques mots)", you_are: "Vous êtes...",
    role_user: "Fidèle", role_preacher: "Prédicateur (à vérifier)", role_artist: "Artiste (à vérifier)", role_influencer: "Influenceur(e) (à vérifier)",
    role_verify_hint: "Tu utilises Yalola comme un fidèle jusqu'à la validation de ton rôle par notre équipe ; le badge Yalola Vérifié (payant) débloque ensuite les avantages définis pour ton rôle.",
    gender: "Sexe", male: "Homme", female: "Femme", birth: "Date de naissance", birth_ph: "JJ/MM/AAAA",
    city: "Ville", country: "Pays", denomination: "Obédience / Dénomination", choose: "Choisir...",
    denom_other_label: "Nom de ton obédience", denom_other_hint: "Sera proposée dans la liste après validation par un administrateur.",
    church_leader: "Responsable de l'église", church: "Nom de l'église",
    church_other: "Mon église n'est pas dans la liste",
    church_optional: "Facultatif — tu pourras ajouter ton église plus tard.", skip: "Passer",
    church_search_hint: "Recherche ton église — si elle n'existe pas encore, tu pourras la suggérer.",
    church_search_placeholder: "Rechercher mon église (nom ou ville)...",
    church_no_results: "Aucune église trouvée.",
    church_selected: "Église sélectionnée :",
    leader_auto: "Rempli automatiquement depuis l'église",
    suggest_title: "Suggère ton église",
    suggest_hint: "Elle sera vérifiée par un modérateur avant d'être publiée.",
    back_to_search: "Revenir à la recherche",
    searching: "Recherche...",
    interests: "Centres d'intérêt", interests_hint: "Choisis au moins 3 centres d'intérêt",
    step1: "Informations personnelles", step2: "Rôle & obédience", step3: "Centres d'intérêt",
    light_hint: "Moins d'une minute — le reste se complète après, en 2 minutes.",
    next: "Suivant", back: "Précédent",
    required_error: "Merci de remplir tous les champs obligatoires",
    interests_min: "Choisis au moins 3 centres d'intérêt",
    optional: "facultatif",
    welcome: "Bienvenue", account_created: "Compte créé !",
    footer: "© Yalola · Belgique · Conforme RGPD · Fait avec foi",
    join_members: "Déjà {count} membres dans la communauté 🙏",
    join_first: "Sois parmi les premiers membres 🙏",
    full_title: "Inscriptions temporairement complètes",
    full_desc: "Nous accueillons les membres par vagues pour garantir une expérience fluide. Reviens très bientôt — laisse-nous ton e-mail auprès de ta communauté 🙏",
    age_min_error: "Tu dois avoir au moins {min} ans pour t'inscrire.",
    age_max_error: "L'âge maximum autorisé pour l'inscription est {max} ans.",
    age_hint_min: "Inscription réservée aux {min} ans et plus.",
  },
  en: {
    subtitle: "The Christian network that unites.",
    desc: "Share your verses, pray together, find a church, and meet brothers and sisters who share your faith.",
    chip_church: "Find a church", chip_match: "Things in common", chip_live: "Live & offerings", chip_groups: "Faith groups",
    choose_lang: "Choose your language",
    login: "Sign in", register: "Create account",
    email: "Email", password: "Password", password_hint: "(6+ chars)",
    logging_in: "Signing in...", creating: "Creating...",
    demo: "",
    first_name: "First name", last_name: "Last name", intro: "Intro (a few words about you)", you_are: "You are...",
    role_user: "Believer", role_preacher: "Preacher (verify)", role_artist: "Artist (verify)", role_influencer: "Influencer (verify)",
    role_verify_hint: "You'll use Yalola as a believer until our team approves your role; the Yalola Vérifié badge (paid) then unlocks the advantages defined for your role.",
    gender: "Gender", male: "Male", female: "Female", birth: "Birth date", birth_ph: "MM/DD/YYYY",
    city: "City", country: "Country", denomination: "Denomination", choose: "Choose...",
    denom_other_label: "Your denomination name", denom_other_hint: "It will appear in the list after an administrator validates it.",
    church_leader: "Church leader", church: "Church name",
    church_other: "My church is not in the list",
    church_optional: "Optional — you can add your church later.", skip: "Skip",
    church_search_hint: "Search for your church — if it doesn't exist yet, you can suggest it.",
    church_search_placeholder: "Search my church (name or city)...",
    church_no_results: "No church found.",
    church_selected: "Selected church:",
    leader_auto: "Auto-filled from the selected church",
    suggest_title: "Suggest your church",
    suggest_hint: "It will be reviewed by a moderator before being published.",
    back_to_search: "Back to search",
    searching: "Searching...",
    interests: "Interests", interests_hint: "Pick at least 3 interests",
    step1: "Personal information", step2: "Role & denomination", step3: "Interests",
    light_hint: "Under a minute — the rest takes 2 minutes after signing up.",
    next: "Next", back: "Back",
    required_error: "Please fill in all required fields",
    interests_min: "Pick at least 3 interests",
    optional: "optional",
    welcome: "Welcome", account_created: "Account created!",
    footer: "© Yalola · Belgium · GDPR compliant · Made with faith",
    join_members: "Already {count} members in the community 🙏",
    join_first: "Be among the very first members 🙏",
    full_title: "Registrations temporarily full",
    full_desc: "We welcome members in waves to keep the experience smooth. Please check back very soon 🙏",
    age_min_error: "You must be at least {min} years old to register.",
    age_max_error: "The maximum allowed age for registration is {max} years.",
    age_hint_min: "Registration is for ages {min} and above.",
  },
  es: {
    subtitle: "La red cristiana que une.",
    desc: "Comparte tus versículos, ora en comunidad, encuentra una iglesia y conoce hermanos y hermanas que comparten tu fe.",
    chip_church: "Encuentra una iglesia", chip_match: "Puntos en común", chip_live: "Directo y ofrendas", chip_groups: "Grupos de fe",
    choose_lang: "Elige tu idioma",
    login: "Iniciar sesión", register: "Crear cuenta",
    email: "Correo electrónico", password: "Contraseña", password_hint: "(6+ car.)",
    logging_in: "Conectando...", creating: "Creando...",
    demo: "",
    first_name: "Nombre", last_name: "Apellido", intro: "Intro (preséntate en pocas palabras)", you_are: "Tú eres...",
    role_user: "Fiel", role_preacher: "Predicador (verificar)", role_artist: "Artista (verificar)", role_influencer: "Influencer (verificar)",
    role_verify_hint: "Usarás Yalola como fiel hasta que nuestro equipo apruebe tu rol; la insignia Yalola Vérifié (de pago) desbloquea después las ventajas definidas para tu rol.",
    gender: "Sexo", male: "Hombre", female: "Mujer", birth: "Fecha de nacimiento", birth_ph: "DD/MM/AAAA",
    city: "Ciudad", country: "País", denomination: "Denominación", choose: "Elegir...",
    denom_other_label: "Nombre de tu denominación", denom_other_hint: "Aparecerá en la lista tras la validación de un administrador.",
    church_leader: "Responsable de la iglesia", church: "Nombre de la iglesia",
    church_other: "Mi iglesia no está en la lista",
    church_optional: "Opcional — puedes añadir tu iglesia más tarde.", skip: "Omitir",
    church_search_hint: "Busca tu iglesia — si aún no existe, podrás sugerirla.",
    church_search_placeholder: "Buscar mi iglesia (nombre o ciudad)...",
    church_no_results: "No se encontró ninguna iglesia.",
    church_selected: "Iglesia seleccionada:",
    leader_auto: "Rellenado automáticamente desde la iglesia",
    suggest_title: "Sugiere tu iglesia",
    suggest_hint: "Será revisada por un moderador antes de publicarse.",
    back_to_search: "Volver a la búsqueda",
    searching: "Buscando...",
    interests: "Intereses", interests_hint: "Elige al menos 3 intereses",
    step1: "Información personal", step2: "Rol y denominación", step3: "Intereses",
    light_hint: "Menos de un minuto — el resto se completa después, en 2 minutos.",
    next: "Siguiente", back: "Atrás",
    required_error: "Completa todos los campos obligatorios",
    interests_min: "Elige al menos 3 intereses",
    optional: "opcional",
    welcome: "Bienvenido", account_created: "¡Cuenta creada!",
    footer: "© Yalola · Bélgica · Conforme RGPD · Hecho con fe",
    join_members: "Ya somos {count} miembros en la comunidad 🙏",
    join_first: "Sé de los primeros miembros 🙏",
    full_title: "Inscripciones temporalmente completas",
    full_desc: "Damos la bienvenida por oleadas para cuidar la experiencia. Vuelve muy pronto 🙏",
    age_min_error: "Debes tener al menos {min} años para registrarte.",
    age_max_error: "La edad máxima permitida para registrarse es {max} años.",
    age_hint_min: "El registro es para {min} años o más.",
  },
  pt: {
    subtitle: "A rede cristã que une.",
    desc: "Partilha os teus versículos, ora em comunidade, encontra uma igreja e conhece irmãos e irmãs que partilham a tua fé.",
    chip_church: "Encontra uma igreja", chip_match: "Pontos em comum", chip_live: "Ao vivo & ofertas", chip_groups: "Grupos de fé",
    choose_lang: "Escolhe o teu idioma",
    login: "Entrar", register: "Criar conta",
    email: "E-mail", password: "Palavra-passe", password_hint: "(6+ car.)",
    logging_in: "A entrar...", creating: "A criar...",
    demo: "",
    first_name: "Nome", last_name: "Apelido", intro: "Intro (apresenta-te em poucas palavras)", you_are: "Tu és...",
    role_user: "Fiel", role_preacher: "Pregador (verificar)", role_artist: "Artista (verificar)", role_influencer: "Influenciador(a) (verificar)",
    role_verify_hint: "Vais usar o Yalola como fiel até a nossa equipa aprovar o teu papel; o selo Yalola Vérifié (pago) desbloqueia depois as vantagens definidas para o teu papel.",
    gender: "Sexo", male: "Homem", female: "Mulher", birth: "Data de nascimento", birth_ph: "DD/MM/AAAA",
    city: "Cidade", country: "País", denomination: "Denominação", choose: "Escolher...",
    denom_other_label: "Nome da tua denominação", denom_other_hint: "Aparecerá na lista após validação de um administrador.",
    church_leader: "Responsável da igreja", church: "Nome da igreja",
    church_other: "A minha igreja não está na lista",
    church_optional: "Opcional — podes adicionar a tua igreja mais tarde.", skip: "Ignorar",
    church_search_hint: "Procura a tua igreja — se ainda não existir, poderás sugeri-la.",
    church_search_placeholder: "Procurar a minha igreja (nome ou cidade)...",
    church_no_results: "Nenhuma igreja encontrada.",
    church_selected: "Igreja selecionada:",
    leader_auto: "Preenchido automaticamente a partir da igreja",
    suggest_title: "Sugere a tua igreja",
    suggest_hint: "Será verificada por um moderador antes de ser publicada.",
    back_to_search: "Voltar à pesquisa",
    searching: "A procurar...",
    interests: "Interesses", interests_hint: "Escolhe pelo menos 3 interesses",
    step1: "Informações pessoais", step2: "Função e denominação", step3: "Interesses",
    light_hint: "Menos de um minuto — o resto completa-se depois, em 2 minutos.",
    next: "Seguinte", back: "Voltar",
    required_error: "Preenche todos os campos obrigatórios",
    interests_min: "Escolhe pelo menos 3 interesses",
    optional: "opcional",
    welcome: "Bem-vindo", account_created: "Conta criada!",
    footer: "© Yalola · Bélgica · Conforme RGPD · Feito com fé",
    join_members: "Já somos {count} membros na comunidade 🙏",
    join_first: "Seja um dos primeiros membros 🙏",
    full_title: "Inscrições temporariamente esgotadas",
    full_desc: "Recebemos membros em ondas para garantir uma boa experiência. Volte muito em breve 🙏",
    age_min_error: "Você deve ter pelo menos {min} anos para se inscrever.",
    age_max_error: "A idade máxima permitida para inscrição é {max} anos.",
    age_hint_min: "Inscrição para {min} anos ou mais.",
  },
};

// Extra strings for OTP verification, forgot-password and legal links (4 languages).
const EXTRA = {
  fr: {
    id_otp_sent: "Un code à 6 chiffres vient d'être envoyé sur WhatsApp au {phone}. Il expire dans 10 minutes.",
    id_remembered_title: "Continuer en tant que {name}", id_remembered_hint: "Dernier compte connecté sur cet appareil", id_remembered_forget: "Ce n'est pas moi", id_or: "ou",
    id_label: "E-mail ou numéro WhatsApp", id_placeholder: "ex. prenom@mail.com ou 081 234 56 78", id_hint: "Un numéro → tu recevras un code sur WhatsApp. Un e-mail → ton mot de passe.",
    id_tag_email: "E-mail", id_tag_wa: "WhatsApp", id_continue: "Continuer", id_checking: "Vérification…", id_invalid: "Entre un e-mail ou un numéro WhatsApp valide.",
    id_new: "Nouveau sur Yalola ?", id_welcome_back: "Bon retour", id_found: "Compte trouvé", id_change: "Changer",
    id_unknown_title: "Pas encore de compte", id_unknown_phone: "Aucun compte Yalola n'utilise ce numéro.", id_unknown_email: "Aucun compte Yalola n'utilise cet e-mail.",
    id_unknown_help: "Vérifie la saisie, ou crée ton compte en 1 minute — il est déjà pré-rempli.", id_register_phone: "Créer mon compte avec ce numéro", id_register_email: "Créer mon compte avec cet e-mail",
    id_retry: "Essayer un autre e-mail / numéro", id_otp_hint: "Ce compte se connecte avec WhatsApp ({hint}). Entre ton numéro pour recevoir le code.", id_pw_hint: "Ce compte se connecte avec un e-mail et un mot de passe ({hint}).",
    id_setpw_title: "Définis un mot de passe", id_setpw_desc: "Ce compte se connecte avec WhatsApp, momentanément indisponible. Un code à 6 chiffres vient d'être envoyé à {email} : définis un mot de passe pour continuer à te connecter avec ton e-mail.",
    id_setpw_email_desc: "Ce compte se connecte avec WhatsApp, momentanément indisponible. Saisis l'e-mail de ton compte ({hint}) : nous t'enverrons un code pour définir un mot de passe.",
    id_setpw_submit: "Définir et me connecter", id_setpw_done: "Mot de passe défini — bon retour !",
    id_recover_title: "Connexion WhatsApp indisponible", id_recover_desc: "Ce compte se connecte uniquement avec WhatsApp, désactivé pour le moment, et n'a pas d'e-mail de secours. Fais une demande de récupération : notre équipe t'aidera à retrouver l'accès.",
    id_recover_cta: "Récupérer mon compte",
    reg_id_hint_email: "Tu confirmeras cette adresse avec un code reçu par e-mail.", reg_wa_off: "L'inscription par WhatsApp est désactivée pour le moment : inscris-toi avec ton adresse e-mail.",
    reg_locked_hint: "Aucun compte n'utilise cet identifiant : il servira à ton inscription.", reg_id_edit: "Modifier",
    reg_draft_restored: "Brouillon repris : tes informations ont été conservées.", reg_draft_restored_pw: "Brouillon repris — il ne reste qu'à ressaisir ton mot de passe.", reg_draft_reset: "Recommencer à zéro",
    id_choose_title: "Comment veux-tu te connecter ?", id_m_whatsapp: "Code WhatsApp", id_m_email_code: "Code par e-mail", id_m_password: "Mot de passe", id_m_password_sub: "Avec ton adresse e-mail ({hint})", id_m_preferred: "Préféré", id_m_last_used: "Dernière fois", id_m_forget: "Oublier ce choix sur cet appareil", id_m_forgotten: "Choix oublié : l'ordre habituel est rétabli.",
    id_other_method: "Utiliser un autre moyen", id_email_form_desc: "Ce compte a une adresse e-mail ({hint}). Saisis-la pour recevoir le code.", id_email_otp_title: "Vérifie ta boîte mail",
    id_email_otp_sent: "Un code à 6 chiffres vient d'être envoyé à {email}. Il expire dans 10 minutes.", id_email_send: "Recevoir le code par e-mail", id_email_verify: "Vérifier et me connecter", id_email_resent: "Nouveau code envoyé par e-mail.",
    demo_code: "Mode démo — code pré-rempli : {code}",
    otp_title: "Vérifie ton e-mail", otp_desc: "Nous avons envoyé un code à 6 chiffres à", otp_placeholder: "000000",
    otp_verify: "Vérifier & créer mon compte", otp_verifying: "Vérification…", otp_resend: "Renvoyer le code", otp_resend_in: "Renvoyer dans {s}s",
    otp_change: "Modifier l'adresse e-mail", otp_sent: "Code envoyé ✉️", otp_check_spam: "Pense à vérifier tes courriers indésirables.",
    forgot_link: "Mot de passe oublié ?", recover_link: "Plus d'accès à ton numéro ou à ton e-mail ? Récupérer mon compte",
    fp_title: "Mot de passe oublié", fp_email_desc: "Saisis ton e-mail : nous t'enverrons un code à 6 chiffres.",
    fp_send: "Envoyer le code", fp_sending: "Envoi…",
    fp_code_title: "Entre le code reçu", fp_code_desc: "Entre le code reçu à",
    fp_new_pass: "Nouveau mot de passe", fp_confirm_pass: "Confirme le mot de passe",
    fp_reset: "Réinitialiser le mot de passe", fp_resetting: "Réinitialisation…",
    fp_success: "Mot de passe mis à jour ! Connecte-toi.", fp_back_login: "Retour à la connexion",
    fp_no_match: "Les mots de passe ne correspondent pas", fp_pass_short: "Mot de passe : 6 caractères minimum",
    legal_terms: "CGU", legal_privacy: "Confidentialité", legal_privacy_full: "Politique de confidentialité", legal_gdpr: "RGPD", legal_delete: "Supprimer mon compte", legal_child: "Sécurité des enfants", discover: "Découvrir",
    accept_prefix: "J'accepte les", accept_and: "et la",
    terms_required: "Tu dois accepter les CGU et la Politique de confidentialité pour continuer.",
    ref_code_label: "Code d'invitation (facultatif)", ref_code_ph: "Ex. W3APJ4", ref_code_hint: "Le code ou le lien d'un ami : vous serez connectés dès ton inscription.", ref_code_from_link: "Invitation reçue — code appliqué", ref_code_bonus: "+{points} points de bienvenue offerts à ton inscription",
    login_legal_prefix: "En te connectant, tu acceptes les",
    zero_tolerance_hint: "Tolérance zéro : aucun contenu inapproprié ni comportement abusif n'est toléré sur Yalola. Les contrevenants sont exclus.",
    email_option: "E-mail", wa_option: "WhatsApp",
    wa_phone_label: "Numéro WhatsApp", wa_phone_invalid: "Numéro WhatsApp invalide.",
    wa_code_sent: "Code envoyé sur WhatsApp 💬",
    wa_unavailable: "L'inscription par WhatsApp est temporairement indisponible. Vous pouvez continuer en utilisant votre adresse e-mail.",
    wa_continue_email: "Continuer avec E-mail",
    wa_login_title: "Connexion par WhatsApp", wa_login_desc: "Reçois un code sur WhatsApp, sans mot de passe.",
    wa_send_code: "Envoyer le code",
    wa_otp_title: "Entre le code WhatsApp", wa_otp_desc: "Nous avons envoyé un code à 6 chiffres sur WhatsApp au",
    wa_verify_login: "Se connecter", wa_change_number: "Modifier le numéro",
    wa_reg_hint: "Sans mot de passe : tu recevras un code sur WhatsApp.",
    dv_title: "Nouvel appareil détecté", dv_desc_email: "Pour ta sécurité, entre le code envoyé à",
    dv_desc_whatsapp: "Pour ta sécurité, entre le code envoyé sur WhatsApp au", dv_verify: "Vérifier cet appareil",
    dv_sent: "Code de sécurité envoyé 🔒",
    fp_sent_title: "Vérifie ta boîte mail", fp_sent_desc: "Si un compte Yalola correspond à {email}, un code à 6 chiffres vient d'être envoyé. Pense à vérifier tes courriers indésirables. Le code expire dans 10 minutes.",
    fp_have_code: "J'ai reçu le code", fp_change_email: "Modifier l'adresse", fp_resent: "Si un compte existe, un nouveau code vient d'être envoyé.",
    fp_nothing: "Rien reçu ? Vérifie l'adresse saisie et tes courriers indésirables.",
    fp_wa_offer: "Tu ne reçois pas l'e-mail ? Recevoir le code sur WhatsApp ({hint})", fp_wa_title: "Code par WhatsApp", fp_wa_desc: "Saisis le numéro WhatsApp de ton compte ({hint}) : le code de réinitialisation y sera envoyé.", fp_wa_send: "Envoyer le code sur WhatsApp", fp_wa_sent_desc: "Un code à 6 chiffres vient d'être envoyé sur WhatsApp au {hint}. Il expire dans 10 minutes.", fp_wa_back_email: "Recevoir le code par e-mail",
    id_unknown_recover: "Tu avais un compte avec un autre numéro ou e-mail que tu n'as plus ? Récupérer mon compte",
    wa_login_otp_desc: "Si un compte Yalola correspond au numéro {phone}, un code à 6 chiffres vient d'être envoyé sur WhatsApp. Il expire dans 10 minutes.",
    wa_resent: "Si un compte existe, un nouveau code vient d'être envoyé sur WhatsApp.",
    login_help: "Connecte-toi avec ton e-mail ou ton numéro WhatsApp. Sur un nouvel appareil, un code de vérification te sera envoyé.",
    register_help: "Crée ton compte en 3 étapes : tes informations, ton rôle et ton église, tes centres d'intérêt. Tu confirmes ensuite ton e-mail ou ton numéro WhatsApp avec un code.",
    register_age: "Tu dois avoir au moins {age} ans pour t'inscrire.",
  },
  en: {
    id_otp_sent: "A 6-digit code was just sent on WhatsApp to {phone}. It expires in 10 minutes.",
    id_remembered_title: "Continue as {name}", id_remembered_hint: "Last account signed in on this device", id_remembered_forget: "Not me", id_or: "or",
    id_label: "E-mail or WhatsApp number", id_placeholder: "e.g. name@mail.com or 081 234 56 78", id_hint: "A number → you'll get a WhatsApp code. An e-mail → your password.",
    id_tag_email: "E-mail", id_tag_wa: "WhatsApp", id_continue: "Continue", id_checking: "Checking…", id_invalid: "Enter a valid e-mail or WhatsApp number.",
    id_new: "New to Yalola?", id_welcome_back: "Welcome back", id_found: "Account found", id_change: "Change",
    id_unknown_title: "No account yet", id_unknown_phone: "No Yalola account uses this number.", id_unknown_email: "No Yalola account uses this e-mail.",
    id_unknown_help: "Check what you typed, or create your account in 1 minute — it's already pre-filled.", id_register_phone: "Create my account with this number", id_register_email: "Create my account with this e-mail",
    id_retry: "Try another e-mail / number", id_otp_hint: "This account signs in with WhatsApp ({hint}). Enter your number to get the code.", id_pw_hint: "This account signs in with an e-mail and a password ({hint}).",
    id_setpw_title: "Set a password", id_setpw_desc: "This account signs in with WhatsApp, which is unavailable right now. We sent a 6-digit code to {email}: set a password to keep signing in with your e-mail.",
    id_setpw_email_desc: "This account signs in with WhatsApp, which is unavailable right now. Enter your account e-mail ({hint}): we'll send you a code to set a password.",
    id_setpw_submit: "Set and sign in", id_setpw_done: "Password set — welcome back!",
    id_recover_title: "WhatsApp sign-in unavailable", id_recover_desc: "This account only signs in with WhatsApp, which is disabled right now, and has no backup e-mail. Send a recovery request: our team will help you get back in.",
    id_recover_cta: "Recover my account",
    reg_id_hint_email: "You'll confirm this address with a code sent by e-mail.", reg_wa_off: "WhatsApp sign-up is turned off for now: sign up with your e-mail address.",
    reg_locked_hint: "No account uses this identifier: it will be used for your sign-up.", reg_id_edit: "Edit",
    reg_draft_restored: "Draft restored: your details were kept.", reg_draft_restored_pw: "Draft restored — just re-enter your password.", reg_draft_reset: "Start over",
    id_choose_title: "How do you want to sign in?", id_m_whatsapp: "WhatsApp code", id_m_email_code: "E-mail code", id_m_password: "Password", id_m_password_sub: "With your e-mail address ({hint})", id_m_preferred: "Preferred", id_m_last_used: "Last time", id_m_forget: "Forget this choice on this device", id_m_forgotten: "Choice forgotten: the usual order is back.",
    id_other_method: "Use another method", id_email_form_desc: "This account has an e-mail address ({hint}). Enter it to receive the code.", id_email_otp_title: "Check your inbox",
    id_email_otp_sent: "A 6-digit code was just sent to {email}. It expires in 10 minutes.", id_email_send: "Get the code by e-mail", id_email_verify: "Verify and sign in", id_email_resent: "New code sent by e-mail.",
    demo_code: "Demo mode — pre-filled code: {code}",
    otp_title: "Verify your email", otp_desc: "We sent a 6-digit code to", otp_placeholder: "000000",
    otp_verify: "Verify & create my account", otp_verifying: "Verifying…", otp_resend: "Resend code", otp_resend_in: "Resend in {s}s",
    otp_change: "Change email address", otp_sent: "Code sent ✉️", otp_check_spam: "Remember to check your spam folder.",
    forgot_link: "Forgot password?", recover_link: "Lost access to your number or e-mail? Recover my account",
    fp_title: "Forgot password", fp_email_desc: "Enter your email: we'll send you a 6-digit code.",
    fp_send: "Send code", fp_sending: "Sending…",
    fp_code_title: "Enter the code", fp_code_desc: "Enter the code received at",
    fp_new_pass: "New password", fp_confirm_pass: "Confirm password",
    fp_reset: "Reset password", fp_resetting: "Resetting…",
    fp_success: "Password updated! Please sign in.", fp_back_login: "Back to sign in",
    fp_no_match: "Passwords do not match", fp_pass_short: "Password: 6 characters minimum",
    legal_terms: "Terms", legal_privacy: "Privacy", legal_privacy_full: "Privacy Policy", legal_gdpr: "GDPR", legal_delete: "Account deletion", legal_child: "Child safety", discover: "Discover",
    accept_prefix: "I accept the", accept_and: "and the",
    terms_required: "You must accept the Terms and Privacy Policy to continue.",
    ref_code_label: "Invitation code (optional)", ref_code_ph: "e.g. W3APJ4", ref_code_hint: "A friend's code or link: you'll be connected as soon as you sign up.", ref_code_from_link: "Invitation received — code applied", ref_code_bonus: "+{points} welcome points when you sign up",
    login_legal_prefix: "By signing in, you agree to the",
    zero_tolerance_hint: "Zero tolerance: no objectionable content or abusive behaviour is tolerated on Yalola. Offenders are removed.",
    email_option: "Email", wa_option: "WhatsApp",
    wa_phone_label: "WhatsApp number", wa_phone_invalid: "Invalid WhatsApp number.",
    wa_code_sent: "Code sent on WhatsApp 💬",
    wa_unavailable: "WhatsApp sign-up is temporarily unavailable. You can continue using your e-mail address.",
    wa_continue_email: "Continue with E-mail",
    wa_login_title: "Sign in with WhatsApp", wa_login_desc: "Get a code on WhatsApp — no password needed.",
    wa_send_code: "Send code",
    wa_otp_title: "Enter the WhatsApp code", wa_otp_desc: "We sent a 6-digit code on WhatsApp to",
    wa_verify_login: "Sign in", wa_change_number: "Change number",
    wa_reg_hint: "No password: you'll get a code on WhatsApp.",
    dv_title: "New device detected", dv_desc_email: "For your security, enter the code sent to",
    dv_desc_whatsapp: "For your security, enter the code sent on WhatsApp to", dv_verify: "Verify this device",
    dv_sent: "Security code sent 🔒",
    fp_sent_title: "Check your inbox", fp_sent_desc: "If a Yalola account matches {email}, a 6-digit code has just been sent. Remember to check your spam folder. The code expires in 10 minutes.",
    fp_have_code: "I received the code", fp_change_email: "Change the address", fp_resent: "If an account exists, a new code has just been sent.",
    fp_nothing: "Nothing received? Check the address you entered and your spam folder.",
    fp_wa_offer: "Not receiving the e-mail? Get the code on WhatsApp ({hint})", fp_wa_title: "Code by WhatsApp", fp_wa_desc: "Enter your account's WhatsApp number ({hint}): the reset code will be sent there.", fp_wa_send: "Send the code on WhatsApp", fp_wa_sent_desc: "A 6-digit code was just sent on WhatsApp to {hint}. It expires in 10 minutes.", fp_wa_back_email: "Get the code by e-mail",
    id_unknown_recover: "Had an account with another number or e-mail you no longer have? Recover my account",
    wa_login_otp_desc: "If a Yalola account matches {phone}, a 6-digit code has just been sent on WhatsApp. It expires in 10 minutes.",
    wa_resent: "If an account exists, a new code has just been sent on WhatsApp.",
    login_help: "Sign in with your e-mail or WhatsApp number. On a new device, a verification code will be sent to you.",
    register_help: "Create your account in 3 steps: your details, your role and church, your interests. You then confirm your e-mail or WhatsApp number with a code.",
    register_age: "You must be at least {age} years old to sign up.",
  },
  es: {
    id_otp_sent: "Acabamos de enviar un código de 6 dígitos por WhatsApp al {phone}. Caduca en 10 minutos.",
    id_remembered_title: "Continuar como {name}", id_remembered_hint: "Última cuenta conectada en este dispositivo", id_remembered_forget: "No soy yo", id_or: "o",
    id_label: "Correo o número de WhatsApp", id_placeholder: "ej. nombre@mail.com o 081 234 56 78", id_hint: "Un número → recibirás un código por WhatsApp. Un correo → tu contraseña.",
    id_tag_email: "Correo", id_tag_wa: "WhatsApp", id_continue: "Continuar", id_checking: "Verificando…", id_invalid: "Escribe un correo o un número de WhatsApp válido.",
    id_new: "¿Nuevo en Yalola?", id_welcome_back: "Bienvenido de nuevo", id_found: "Cuenta encontrada", id_change: "Cambiar",
    id_unknown_title: "Aún no tienes cuenta", id_unknown_phone: "Ninguna cuenta de Yalola usa este número.", id_unknown_email: "Ninguna cuenta de Yalola usa este correo.",
    id_unknown_help: "Revisa lo escrito, o crea tu cuenta en 1 minuto — ya está prellenada.", id_register_phone: "Crear mi cuenta con este número", id_register_email: "Crear mi cuenta con este correo",
    id_retry: "Probar otro correo / número", id_otp_hint: "Esta cuenta inicia sesión con WhatsApp ({hint}). Escribe tu número para recibir el código.", id_pw_hint: "Esta cuenta inicia sesión con correo y contraseña ({hint}).",
    id_setpw_title: "Define una contraseña", id_setpw_desc: "Esta cuenta inicia sesión con WhatsApp, no disponible por ahora. Enviamos un código de 6 dígitos a {email}: define una contraseña para seguir entrando con tu correo.",
    id_setpw_email_desc: "Esta cuenta inicia sesión con WhatsApp, no disponible por ahora. Escribe el correo de tu cuenta ({hint}): te enviaremos un código para definir una contraseña.",
    id_setpw_submit: "Definir y entrar", id_setpw_done: "Contraseña definida — ¡bienvenido/a de nuevo!",
    id_recover_title: "Inicio con WhatsApp no disponible", id_recover_desc: "Esta cuenta solo inicia sesión con WhatsApp, desactivado por ahora, y no tiene correo de respaldo. Haz una solicitud de recuperación: nuestro equipo te ayudará a recuperar el acceso.",
    id_recover_cta: "Recuperar mi cuenta",
    reg_id_hint_email: "Confirmarás esta dirección con un código enviado por correo.", reg_wa_off: "El registro por WhatsApp está desactivado por ahora: regístrate con tu correo electrónico.",
    reg_locked_hint: "Ninguna cuenta usa este identificador: servirá para tu registro.", reg_id_edit: "Modificar",
    reg_draft_restored: "Borrador recuperado: tus datos se han conservado.", reg_draft_restored_pw: "Borrador recuperado — solo falta volver a escribir tu contraseña.", reg_draft_reset: "Empezar de cero",
    id_choose_title: "¿Cómo quieres iniciar sesión?", id_m_whatsapp: "Código WhatsApp", id_m_email_code: "Código por correo", id_m_password: "Contraseña", id_m_password_sub: "Con tu correo electrónico ({hint})", id_m_preferred: "Preferido", id_m_last_used: "Última vez", id_m_forget: "Olvidar esta elección en este dispositivo", id_m_forgotten: "Elección olvidada: vuelve el orden habitual.",
    id_other_method: "Usar otro método", id_email_form_desc: "Esta cuenta tiene un correo electrónico ({hint}). Escríbelo para recibir el código.", id_email_otp_title: "Revisa tu correo",
    id_email_otp_sent: "Acabamos de enviar un código de 6 dígitos a {email}. Caduca en 10 minutos.", id_email_send: "Recibir el código por correo", id_email_verify: "Verificar e iniciar sesión", id_email_resent: "Nuevo código enviado por correo.",
    demo_code: "Modo demo — código prellenado: {code}",
    otp_title: "Verifica tu correo", otp_desc: "Enviamos un código de 6 dígitos a", otp_placeholder: "000000",
    otp_verify: "Verificar y crear mi cuenta", otp_verifying: "Verificando…", otp_resend: "Reenviar código", otp_resend_in: "Reenviar en {s}s",
    otp_change: "Cambiar el correo", otp_sent: "Código enviado ✉️", otp_check_spam: "Revisa tu carpeta de spam.",
    forgot_link: "¿Olvidaste tu contraseña?", recover_link: "¿Sin acceso a tu número o correo? Recuperar mi cuenta",
    fp_title: "Contraseña olvidada", fp_email_desc: "Escribe tu correo: te enviaremos un código de 6 dígitos.",
    fp_send: "Enviar código", fp_sending: "Enviando…",
    fp_code_title: "Introduce el código", fp_code_desc: "Introduce el código recibido en",
    fp_new_pass: "Nueva contraseña", fp_confirm_pass: "Confirma la contraseña",
    fp_reset: "Restablecer contraseña", fp_resetting: "Restableciendo…",
    fp_success: "¡Contraseña actualizada! Inicia sesión.", fp_back_login: "Volver a iniciar sesión",
    fp_no_match: "Las contraseñas no coinciden", fp_pass_short: "Contraseña: mínimo 6 caracteres",
    legal_terms: "Términos", legal_privacy: "Privacidad", legal_privacy_full: "Política de privacidad", legal_gdpr: "RGPD", legal_delete: "Eliminar cuenta", legal_child: "Seguridad infantil", discover: "Descubrir",
    accept_prefix: "Acepto los", accept_and: "y la",
    terms_required: "Debes aceptar los Términos y la Política de privacidad para continuar.",
    ref_code_label: "Código de invitación (opcional)", ref_code_ph: "Ej. W3APJ4", ref_code_hint: "El código o enlace de un amigo: estarán conectados desde tu registro.", ref_code_from_link: "Invitación recibida — código aplicado", ref_code_bonus: "+{points} puntos de bienvenida al registrarte",
    login_legal_prefix: "Al iniciar sesión, aceptas los",
    zero_tolerance_hint: "Tolerancia cero: en Yalola no se tolera ningún contenido inapropiado ni comportamiento abusivo. Los infractores son expulsados.",
    email_option: "Correo", wa_option: "WhatsApp",
    wa_phone_label: "Número de WhatsApp", wa_phone_invalid: "Número de WhatsApp no válido.",
    wa_code_sent: "Código enviado por WhatsApp 💬",
    wa_unavailable: "El registro por WhatsApp no está disponible temporalmente. Puedes continuar usando tu correo electrónico.",
    wa_continue_email: "Continuar con correo",
    wa_login_title: "Entrar con WhatsApp", wa_login_desc: "Recibe un código por WhatsApp, sin contraseña.",
    wa_send_code: "Enviar código",
    wa_otp_title: "Introduce el código de WhatsApp", wa_otp_desc: "Enviamos un código de 6 dígitos por WhatsApp al",
    wa_verify_login: "Entrar", wa_change_number: "Cambiar número",
    wa_reg_hint: "Sin contraseña: recibirás un código por WhatsApp.",
    dv_title: "Nuevo dispositivo detectado", dv_desc_email: "Por tu seguridad, introduce el código enviado a",
    dv_desc_whatsapp: "Por tu seguridad, introduce el código enviado por WhatsApp al", dv_verify: "Verificar este dispositivo",
    dv_sent: "Código de seguridad enviado 🔒",
    fp_sent_title: "Revisa tu correo", fp_sent_desc: "Si una cuenta de Yalola corresponde a {email}, se acaba de enviar un código de 6 dígitos. Revisa también tu carpeta de spam. El código expira en 10 minutos.",
    fp_have_code: "Ya recibí el código", fp_change_email: "Cambiar la dirección", fp_resent: "Si existe una cuenta, se acaba de enviar un nuevo código.",
    fp_nothing: "¿No recibiste nada? Comprueba la dirección escrita y tu carpeta de spam.",
    fp_wa_offer: "¿No recibes el correo? Recibir el código por WhatsApp ({hint})", fp_wa_title: "Código por WhatsApp", fp_wa_desc: "Escribe el número de WhatsApp de tu cuenta ({hint}): el código de restablecimiento se enviará allí.", fp_wa_send: "Enviar el código por WhatsApp", fp_wa_sent_desc: "Se acaba de enviar un código de 6 dígitos por WhatsApp al {hint}. Caduca en 10 minutos.", fp_wa_back_email: "Recibir el código por correo",
    id_unknown_recover: "¿Tenías una cuenta con otro número o correo que ya no tienes? Recuperar mi cuenta",
    wa_login_otp_desc: "Si una cuenta de Yalola corresponde al número {phone}, se acaba de enviar un código de 6 dígitos por WhatsApp. Expira en 10 minutos.",
    wa_resent: "Si existe una cuenta, se acaba de enviar un nuevo código por WhatsApp.",
    login_help: "Inicia sesión con tu correo o tu número de WhatsApp. En un dispositivo nuevo recibirás un código de verificación.",
    register_help: "Crea tu cuenta en 3 pasos: tus datos, tu rol e iglesia, tus intereses. Después confirmas tu correo o tu número de WhatsApp con un código.",
    register_age: "Debes tener al menos {age} años para registrarte.",
  },
  pt: {
    id_otp_sent: "Acabámos de enviar um código de 6 dígitos no WhatsApp para {phone}. Expira em 10 minutos.",
    id_remembered_title: "Continuar como {name}", id_remembered_hint: "Última conta ligada neste dispositivo", id_remembered_forget: "Não sou eu", id_or: "ou",
    id_label: "E-mail ou número de WhatsApp", id_placeholder: "ex. nome@mail.com ou 081 234 56 78", id_hint: "Um número → recebes um código no WhatsApp. Um e-mail → a tua palavra-passe.",
    id_tag_email: "E-mail", id_tag_wa: "WhatsApp", id_continue: "Continuar", id_checking: "A verificar…", id_invalid: "Indica um e-mail ou um número de WhatsApp válido.",
    id_new: "Novo no Yalola?", id_welcome_back: "Bem-vindo de volta", id_found: "Conta encontrada", id_change: "Alterar",
    id_unknown_title: "Ainda sem conta", id_unknown_phone: "Nenhuma conta Yalola usa este número.", id_unknown_email: "Nenhuma conta Yalola usa este e-mail.",
    id_unknown_help: "Confirma o que escreveste, ou cria a tua conta em 1 minuto — já está pré-preenchida.", id_register_phone: "Criar a minha conta com este número", id_register_email: "Criar a minha conta com este e-mail",
    id_retry: "Tentar outro e-mail / número", id_otp_hint: "Esta conta entra com WhatsApp ({hint}). Indica o teu número para receber o código.", id_pw_hint: "Esta conta entra com e-mail e palavra-passe ({hint}).",
    id_setpw_title: "Define uma palavra-passe", id_setpw_desc: "Esta conta entra com WhatsApp, indisponível de momento. Enviámos um código de 6 dígitos para {email}: define uma palavra-passe para continuares a entrar com o teu e-mail.",
    id_setpw_email_desc: "Esta conta entra com WhatsApp, indisponível de momento. Indica o e-mail da tua conta ({hint}): enviaremos um código para definires uma palavra-passe.",
    id_setpw_submit: "Definir e entrar", id_setpw_done: "Palavra-passe definida — bem-vindo(a) de volta!",
    id_recover_title: "Entrada por WhatsApp indisponível", id_recover_desc: "Esta conta entra apenas com WhatsApp, desativado de momento, e não tem e-mail de recurso. Faz um pedido de recuperação: a nossa equipa ajuda-te a recuperar o acesso.",
    id_recover_cta: "Recuperar a minha conta",
    reg_id_hint_email: "Vais confirmar este endereço com um código enviado por e-mail.", reg_wa_off: "O registo por WhatsApp está desativado de momento: regista-te com o teu e-mail.",
    reg_locked_hint: "Nenhuma conta usa este identificador: será usado no teu registo.", reg_id_edit: "Alterar",
    reg_draft_restored: "Rascunho retomado: os teus dados foram guardados.", reg_draft_restored_pw: "Rascunho retomado — falta apenas voltar a escrever a tua palavra-passe.", reg_draft_reset: "Recomeçar do zero",
    id_choose_title: "Como queres entrar?", id_m_whatsapp: "Código WhatsApp", id_m_email_code: "Código por e-mail", id_m_password: "Palavra-passe", id_m_password_sub: "Com o teu e-mail ({hint})", id_m_preferred: "Preferido", id_m_last_used: "Última vez", id_m_forget: "Esquecer esta escolha neste dispositivo", id_m_forgotten: "Escolha esquecida: a ordem habitual voltou.",
    id_other_method: "Usar outro meio", id_email_form_desc: "Esta conta tem um e-mail ({hint}). Indica-o para receberes o código.", id_email_otp_title: "Verifica a tua caixa de e-mail",
    id_email_otp_sent: "Acabámos de enviar um código de 6 dígitos para {email}. Expira em 10 minutos.", id_email_send: "Receber o código por e-mail", id_email_verify: "Verificar e entrar", id_email_resent: "Novo código enviado por e-mail.",
    demo_code: "Modo demo — código pré-preenchido: {code}",
    otp_title: "Verifica o teu e-mail", otp_desc: "Enviámos um código de 6 dígitos para", otp_placeholder: "000000",
    otp_verify: "Verificar e criar a conta", otp_verifying: "A verificar…", otp_resend: "Reenviar código", otp_resend_in: "Reenviar em {s}s",
    otp_change: "Alterar o e-mail", otp_sent: "Código enviado ✉️", otp_check_spam: "Verifica a pasta de spam.",
    forgot_link: "Esqueceste a palavra-passe?", recover_link: "Sem acesso ao teu número ou e-mail? Recuperar a minha conta",
    fp_title: "Palavra-passe esquecida", fp_email_desc: "Escreve o teu e-mail: enviaremos um código de 6 dígitos.",
    fp_send: "Enviar código", fp_sending: "A enviar…",
    fp_code_title: "Introduz o código", fp_code_desc: "Introduz o código recebido em",
    fp_new_pass: "Nova palavra-passe", fp_confirm_pass: "Confirma a palavra-passe",
    fp_reset: "Redefinir palavra-passe", fp_resetting: "A redefinir…",
    fp_success: "Palavra-passe atualizada! Inicia sessão.", fp_back_login: "Voltar a entrar",
    fp_no_match: "As palavras-passe não coincidem", fp_pass_short: "Palavra-passe: mínimo 6 caracteres",
    legal_terms: "Termos", legal_privacy: "Privacidade", legal_privacy_full: "Política de privacidade", legal_gdpr: "RGPD", legal_delete: "Excluir conta", legal_child: "Segurança infantil", discover: "Descobrir",
    accept_prefix: "Aceito os", accept_and: "e a",
    terms_required: "Deves aceitar os Termos e a Política de privacidade para continuar.",
    ref_code_label: "Código de convite (opcional)", ref_code_ph: "Ex. W3APJ4", ref_code_hint: "O código ou link de um amigo: ficarão ligados desde a tua inscrição.", ref_code_from_link: "Convite recebido — código aplicado", ref_code_bonus: "+{points} pontos de boas-vindas na inscrição",
    login_legal_prefix: "Ao entrar, aceitas os",
    zero_tolerance_hint: "Tolerância zero: nenhum conteúdo impróprio nem comportamento abusivo é tolerado no Yalola. Os infratores são excluídos.",
    email_option: "E-mail", wa_option: "WhatsApp",
    wa_phone_label: "Número de WhatsApp", wa_phone_invalid: "Número de WhatsApp inválido.",
    wa_code_sent: "Código enviado no WhatsApp 💬",
    wa_unavailable: "O registo por WhatsApp está temporariamente indisponível. Podes continuar usando o teu e-mail.",
    wa_continue_email: "Continuar com E-mail",
    wa_login_title: "Entrar com WhatsApp", wa_login_desc: "Recebe um código no WhatsApp, sem palavra-passe.",
    wa_send_code: "Enviar código",
    wa_otp_title: "Introduz o código do WhatsApp", wa_otp_desc: "Enviámos um código de 6 dígitos no WhatsApp para",
    wa_verify_login: "Entrar", wa_change_number: "Alterar número",
    wa_reg_hint: "Sem palavra-passe: vais receber um código no WhatsApp.",
    dv_title: "Novo dispositivo detetado", dv_desc_email: "Para tua segurança, introduz o código enviado para",
    dv_desc_whatsapp: "Para tua segurança, introduz o código enviado no WhatsApp para", dv_verify: "Verificar este dispositivo",
    dv_sent: "Código de segurança enviado 🔒",
    fp_sent_title: "Verifica a tua caixa de e-mail", fp_sent_desc: "Se existir uma conta Yalola para {email}, acabou de ser enviado um código de 6 dígitos. Verifica também a pasta de spam. O código expira em 10 minutos.",
    fp_have_code: "Recebi o código", fp_change_email: "Alterar o endereço", fp_resent: "Se existir uma conta, acabou de ser enviado um novo código.",
    fp_nothing: "Não recebeste nada? Verifica o endereço introduzido e a pasta de spam.",
    fp_wa_offer: "Não recebes o e-mail? Receber o código no WhatsApp ({hint})", fp_wa_title: "Código por WhatsApp", fp_wa_desc: "Introduz o número WhatsApp da tua conta ({hint}): o código de reposição será enviado para lá.", fp_wa_send: "Enviar o código no WhatsApp", fp_wa_sent_desc: "Acabou de ser enviado um código de 6 dígitos no WhatsApp para {hint}. Expira em 10 minutos.", fp_wa_back_email: "Receber o código por e-mail",
    id_unknown_recover: "Tinhas uma conta com outro número ou e-mail que já não tens? Recuperar a minha conta",
    wa_login_otp_desc: "Se existir uma conta Yalola para o número {phone}, acabou de ser enviado um código de 6 dígitos no WhatsApp. Expira em 10 minutos.",
    wa_resent: "Se existir uma conta, acabou de ser enviado um novo código no WhatsApp.",
    login_help: "Inicia sessão com o teu e-mail ou número WhatsApp. Num novo dispositivo, receberás um código de verificação.",
    register_help: "Cria a tua conta em 3 passos: os teus dados, o teu papel e igreja, os teus interesses. Depois confirmas o teu e-mail ou número WhatsApp com um código.",
    register_age: "Tens de ter pelo menos {age} anos para te inscrever.",
  },
};

// Whole years from a 'YYYY-MM-DD' date string (null if missing/invalid).
const ageFromBirth = (bd) => {
  if (!bd) return null;
  const d = new Date(bd);
  if (isNaN(d.getTime())) return null;
  const t = new Date();
  let a = t.getFullYear() - d.getFullYear();
  const m = t.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && t.getDate() < d.getDate())) a -= 1;
  return a;
};


export const Onboarding = () => {
  const { lang, setLang, user, login, asError, config, t,
    startRegistration, resendRegistration, verifyRegistration,
    startPhoneRegistration, resendPhoneRegistration, verifyPhoneRegistration,
    startPhoneLogin, verifyPhoneLogin, verifyDevice, startEmailLogin, verifyEmailLogin,
    forgotPassword, resendForgot, verifyForgot, resetPassword } = useApp();
  const nav = useNavigate();
  // Lot 562 — brouillon d'inscription de la session (Inscription Express) : lu une fois au montage.
  const [draft] = useState(() => loadRegisterDraft());
  const [draftRestored, setDraftRestored] = useState(!!draft);
  const [tab, setTab] = useState(draft ? "register" : "login");
  // Écrans connexion / inscription : jamais indexés, canonique = la page d'accueil publique.
  useSeo({ title: tab === "register" ? t("onboarding.register", "Créer un compte") : t("onboarding.login", "Se connecter"), robots: "noindex,follow", canonical: "/", lang }, [tab, lang]);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(draft?.step || 1);
  const [stats, setStats] = useState(null);
  const [refCode, setRefCode] = useState(() => storedRefCode()); // lot 705 — code mémorisé (lien ouvert plus tôt / app installée après)
  const [refFromLink, setRefFromLink] = useState(() => !!storedRefCode());
  // Shared link opened signed out → the destination is stashed (guestGate / RequireAuth): say so.
  useEffect(() => {
    try {
      const qs = new URLSearchParams(window.location.search);
      const r = normalizeRefCode(qs.get("ref"));
      if (r) { setRefCode(r); setRefFromLink(true); storeRefCode(r); setTab("register"); }
      const tb = qs.get("tab");
      if (tb === "login" || tb === "register") setTab(tb);
    } catch { /* noop */ }
  }, []);

  // Auth method (email vs passwordless WhatsApp)
  const [loginMethod, setLoginMethod] = useState("email"); // email | whatsapp (anciennes pages de connexion)
  const [regMethod, setRegMethod] = useState(draft?.regMethod || "email"); // email | whatsapp
  const [waUnavailable, setWaUnavailable] = useState(false); // WhatsApp configuré mais Meta refuse l'envoi → propose le repli e-mail

  // iOS 26+ : tranche d'âge Apple lue à l'ouverture de « Créer un compte », AVANT le formulaire
  // (idle | checking | ok | blocked). < 13 ou partage requis refusé → mur (AgeAssuranceGate), pas de formulaire.
  const [ageCheck, setAgeCheck] = useState("idle");
  useEffect(() => {
    if (tab !== "register" || !isIosNative() || ageCheck !== "idle") return;
    setAgeCheck("checking");
    checkAgeRange().then((r) => {
      if (isBlocking(r.verdict)) { setAgeBlock({ verdict: r.verdict, rec: r, reason: "register" }); setAgeCheck("blocked"); }
      else setAgeCheck("ok");
    }).catch(() => setAgeCheck("ok"));
  }, [tab, ageCheck]);
  useEffect(() => {
    const onBlock = (e) => { if (!e.detail) setAgeCheck((s) => (s === "blocked" ? "ok" : s)); };
    window.addEventListener(AGE_BLOCK_EVENT, onBlock);
    return () => window.removeEventListener(AGE_BLOCK_EVENT, onBlock);
  }, []);

  // OTP registration state
  const [regView, setRegView] = useState("form"); // form | otp
  const [regOtpChannel, setRegOtpChannel] = useState("email"); // email | whatsapp
  const [otpEmail, setOtpEmail] = useState("");
  const [otpPhone, setOtpPhone] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [otpLoading, setOtpLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [regPhone, setRegPhone] = useState(draft?.regPhone || { iso: "", national: "", e164: "" });

  // WhatsApp passwordless login
  const [waLoginPhone, setWaLoginPhone] = useState({ iso: "", national: "", e164: "" });
  const [waLoginView, setWaLoginView] = useState("form"); // form | otp
  const [waLoginCode, setWaLoginCode] = useState("");
  const [waLoginCooldown, setWaLoginCooldown] = useState(0);
  // Lot 563 — connexion par code e-mail (sans mot de passe) : e-mail (connu ou à saisir) → code → connecté.
  const [emLoginEmail, setEmLoginEmail] = useState("");
  const [emLoginView, setEmLoginView] = useState("form"); // form | otp
  const [emLoginCode, setEmLoginCode] = useState("");
  const [emLoginCooldown, setEmLoginCooldown] = useState(0);

  // Default dialing-country detection: timezone (offline, accurate) → locale → edge IP override.
  const [detectedIso, setDetectedIso] = useState(null);
  useEffect(() => {
    const guess = detectClientCountry();
    if (guess && DIAL_CODES[guess]) setDetectedIso(guess);
    api.get("/geo/ip-country").then(({ data }) => {
      const c = (data?.country || "").toUpperCase();
      if (c && DIAL_CODES[c]) setDetectedIso(c);
    }).catch(() => {});
  }, []);

  // New-device step-up verification (email login from an untrusted device)
  const [deviceVerify, setDeviceVerify] = useState(null); // { channel, hint, email, phone } | null
  const [deviceCode, setDeviceCode] = useState("");
  const [deviceLoading, setDeviceLoading] = useState(false);

  // Forgot-password state
  const [forgotStep, setForgotStep] = useState(null); // null | email | otp | reset
  const [fpEmail, setFpEmail] = useState("");
  // Lot 604 — « Je ne reçois pas l'e-mail » : code par WhatsApp (numéro ressaisi, doit être celui du compte)
  const [fpChannel, setFpChannel] = useState("email"); // email | whatsapp
  const [fpWaOpen, setFpWaOpen] = useState(false);
  const [fpWaPhone, setFpWaPhone] = useState(null);
  const [fpCode, setFpCode] = useState("");
  const [fpToken, setFpToken] = useState("");
  const [fpNewPass, setFpNewPass] = useState("");
  const [fpConfirm, setFpConfirm] = useState("");
  const [fpLoading, setFpLoading] = useState(false);
  const [fpCooldown, setFpCooldown] = useState(0);

  useEffect(() => {
    api.get("/auth/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  // Countdown ticker for resend cooldowns.
  useEffect(() => {
    if (cooldown <= 0 && fpCooldown <= 0 && waLoginCooldown <= 0 && emLoginCooldown <= 0) return;
    const h = setInterval(() => {
      setCooldown((c) => (c > 0 ? c - 1 : 0));
      setFpCooldown((c) => (c > 0 ? c - 1 : 0));
      setWaLoginCooldown((c) => (c > 0 ? c - 1 : 0));
      setEmLoginCooldown((c) => (c > 0 ? c - 1 : 0));
    }, 1000);
    return () => clearInterval(h);
  }, [cooldown, fpCooldown, waLoginCooldown, emLoginCooldown]);

  // Admin-editable override first (Admin -> Traductions, key "onboarding.<key>"),
  // then the built-in per-language dictionary as a safe fallback.
  const tr = (key) => {
    const dbv = config?.translations?.[`onboarding.${key}`];
    if (dbv) return dbv;
    return (L10N[lang] || L10N.fr)[key] || (EXTRA[lang] || EXTRA.fr)[key] || L10N.fr[key] || EXTRA.fr[key] || key;
  };
  const waAuthEnabled = config?.settings?.whatsapp_auth_enabled !== false;
  // Interrupteur admin (Réglages › Inscription & connexion) : connexion unique (un seul champ) ou anciennes
  // pages (choix E-mail / WhatsApp). Lu à chaque chargement via /config/public → retour arrière sans mise à jour.
  const unifiedLogin = config?.settings?.unified_login_enabled !== false;

  // Admin-controlled "featured" auth method (which of email/WhatsApp is shown first & pre-selected).
  const loginDefaultMethod = config?.settings?.login_default_method === "whatsapp" ? "whatsapp" : "email";
  const registerDefaultMethod = config?.settings?.register_default_method === "whatsapp" ? "whatsapp" : "email";
  const authDefaultsApplied = useRef(false);
  useEffect(() => {
    if (authDefaultsApplied.current) return;
    // Wait for the REAL /config/public (AppContext seeds config.settings={} initially).
    if (!config?.settings || Object.keys(config.settings).length === 0) return;
    authDefaultsApplied.current = true;
    if (waAuthEnabled) {
      setLoginMethod(loginDefaultMethod);
      setRegMethod(registerDefaultMethod);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config]);
  const loginMethodOrder = loginDefaultMethod === "whatsapp" ? ["whatsapp", "email"] : ["email", "whatsapp"];
  const registerMethodOrder = registerDefaultMethod === "whatsapp" ? ["whatsapp", "email"] : ["email", "whatsapp"];
  const methodMeta = { email: [tr("email_option"), Mail], whatsapp: [tr("wa_option"), MessageCircle] };
  const waLoginDefaultIso = detectedIso || "CD";
  const at = (field, fallback) => {
    const v = config?.settings?.auth_texts?.[field]?.[lang];
    return v && v.trim() ? v : fallback;
  };
  // Raw override (may be "" to explicitly hide a line); undefined = not customized.
  const rawAuth = (field) => config?.settings?.auth_texts?.[field]?.[lang];
  const rf = config?.settings?.registration_fields || {};
  // Lot 580 — inscription allégée (bascule admin, livrée activée) : UNE étape = identifiant (+ mot de passe si e-mail),
  // prénom, nom, obédience, date de naissance, CGU ; genre, pays, ville, rôle et centres d'intérêt se complètent après.
  const lightSignup = config?.settings?.registration_light !== false;
  const lastStep = lightSignup ? 1 : 3;
  // Brouillon d'un ancien formulaire en 3 étapes → en mode allégé, tout tient à l'étape 1.
  useEffect(() => { if (lightSignup && step > 1) setStep(1); }, [lightSignup]); // eslint-disable-line react-hooks/exhaustive-deps
  const ALWAYS_REQUIRED = ["gender", "city", "interests"];
  const reqOf = (k) => ALWAYS_REQUIRED.includes(k) || (rf[k] || "required") === "required";
  const req = (k) => (reqOf(k) ? " *" : ` (${tr("optional")})`);
  const countries = countryList(lang);
  const footerCountry = (() => {
    const code = config?.settings?.country_of_legislation || "BE";
    const found = countries.find((c) => c.code === code);
    return found ? found.label : null;
  })();
  const footerRaw = rawAuth("footer");
  const footerBase = (footerRaw !== undefined && footerRaw !== null) ? footerRaw : tr("footer");
  const footerText = (footerCountry && footerBase) ? footerBase.replace(/Belgi\w*|Bélgica/i, footerCountry) : footerBase;
  const denoms = config?.denominations?.length ? config.denominations.map((d) => d.name) : FALLBACK_DENOMS;

  const ROLES_CHOICE = [
    { key: "user", label: tr("role_user"), icon: Users },
    { key: "preacher", label: tr("role_preacher"), icon: HeartHandshake },
    { key: "artist", label: tr("role_artist"), icon: Radio },
    { key: "influencer", label: tr("role_influencer"), icon: Star },
  ];

  // Connexion unique (lot 550) : un seul champ e-mail / numéro WhatsApp, puis mot de passe ou code selon le compte.
  const [idStep, setIdStep] = useState("identify"); // identify | password | otp | unknown | recover
  const [fpMode, setFpMode] = useState(null); // null (mot de passe oublié) | "set" (garde-fou : compte sans mot de passe, WhatsApp indisponible)
  // Lot 560 — vérification en direct des numéros (longueur plausible pour le pays) : bloque l'envoi d'un code payant.
  const [idCheck, setIdCheck] = useState(EMPTY_CHECK);
  const [waCheck, setWaCheck] = useState(EMPTY_CHECK);
  const [regCheck, setRegCheck] = useState(EMPTY_CHECK);
  // Vérification ferme (sans anti-rebond) juste avant d'envoyer : renvoie le message d'erreur ou null.
  const phoneBlockMessage = async (candidate, iso) => {
    try {
      const c = await checkPhone(candidate, iso);
      return isBlockingCheck(c) ? phoneIssueMessage(c, t, lang) : null;
    } catch { return null; }
  };
  const [identifier, setIdentifier] = useState("");
  const [identifierIso, setIdentifierIso] = useState("");
  const [identity, setIdentity] = useState(null); // réponse de /auth/identify
  // Lot 561 — inscription harmonisée : un seul champ « e-mail ou numéro WhatsApp » (même détection que la page 1) ;
  // arrivée depuis la page 1 (identifiant inconnu) → champ verrouillé + « Modifier » qui ramène à la page 1.
  const [regIdentifier, setRegIdentifier] = useState(draft?.regIdentifier || "");
  const [regIdentifierIso, setRegIdentifierIso] = useState(draft?.regIdentifierIso || "");
  const [regLocked, setRegLocked] = useState(!!draft?.regLocked);
  const [remembered, setRemembered] = useState(() => loadRememberedIdentifier()); // dernier compte connecté ici (lot 552)
  // Anciennes pages : l'e-mail mémorisé est simplement pré-rempli.
  const [loginData, setLoginData] = useState(() => ({ email: loadRememberedIdentifier()?.kind === "email" ? loadRememberedIdentifier().value : "", password: "" }));
  // App native : CGU + Politique déjà acceptées sur l'écran « Bienvenue » (1er lancement) → pas de case ni de rappel ici.
  const consentDone = nativeConsentFlow();
  // Consignes (app native, modifiables dans Admin › Traductions : onboarding.login_help / register_help / register_age).
  const minAge = Number(config?.settings?.min_registration_age || 0);
  const registerHelp = [t("onboarding.register_help", tr("register_help")), minAge > 0 ? t("onboarding.register_age", tr("register_age")).replace("{age}", String(minAge)) : ""].filter(Boolean).join(" ");
  const [acceptTerms, setAcceptTerms] = useState(consentDone);
  const EMPTY_REG = {
    email: "", password: "", first_name: "", last_name: "", role: "user", gender: "male",
    denomination: "", church_leader: "", church_name: "", city: "", country: "", interests: [], birth_date: "",
  };
  const [regData, setRegData] = useState(() => ({ ...EMPTY_REG, ...(draft?.regData || {}), password: "" }));
  const set = (k, v) => setRegData((d) => ({ ...d, [k]: v }));
  // Inscription harmonisée : la méthode (e-mail ou WhatsApp) découle de ce qui est tapé dans le champ unique ;
  // anciennes pages (interrupteur admin) → sélecteur E-mail / WhatsApp inchangé.
  const regKind = unifiedLogin ? detectIdentifierKind(regIdentifier) : "";
  const regMethodEff = unifiedLogin ? (regKind === "phone" ? "whatsapp" : "email") : regMethod;
  const regEmailEff = unifiedLogin ? (regKind === "email" ? regIdentifier.trim().toLowerCase() : "") : regData.email;
  const regPhoneEff = unifiedLogin
    ? (regKind === "phone" ? phoneFromInput(regIdentifier, regIdentifierIso || detectedIso || regData.country || "CD") : { iso: "", national: "", e164: "" })
    : regPhone;
  const regWaOff = unifiedLogin && regKind === "phone" && !waAuthEnabled; // numéro tapé alors que WhatsApp est désactivé
  // Inscription Express : le mot de passe n'est jamais conservé → une inscription e-mail reprend à l'étape 1 (ressaisie).
  const draftNeedsPassword = !!draft && regMethodEff === "email";
  useEffect(() => { if (draftNeedsPassword && step > 1) setStep(1); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Sauvegarde du brouillon à chaque frappe (sessionStorage ; jamais le mot de passe ni la case CGU) ; vidé quand plus rien n'est saisi.
  useEffect(() => {
    if (regView === "otp") return;
    saveRegisterDraft({ regData, regIdentifier, regIdentifierIso, regLocked, regPhone, regMethod, step });
  }, [regData, regIdentifier, regIdentifierIso, regLocked, regPhone, regMethod, step, regView]);
  const resetRegisterDraft = () => {
    clearRegisterDraft(); setDraftRestored(false);
    setRegData({ ...EMPTY_REG }); setRegIdentifier(""); setRegIdentifierIso(""); setRegLocked(false);
    setRegPhone({ iso: "", national: "", e164: "" }); setStep(1); setAcceptTerms(consentDone);
  };

  useEffect(() => { if (user) { clearRegisterDraft(); nav("/home"); } }, [user, nav]);

  // Demo mode only: when the backend echoes the OTP (OTP_DEBUG on preview), show it
  // on screen so the flow can be tested without live WhatsApp delivery. In production
  // the real WhatsApp path never returns a code, so this toast never appears.
  const showDevCode = (data) => {
    const code = data && data.dev_code;
    if (code) toast.success(tr("demo_code").replace("{code}", code), { duration: 30000, position: "top-center" });
  };

  // Lot 564 : après une connexion réussie, retenir le canal pour ce compte (toutes ses clés connues) sur cet appareil.
  const rememberMethod = (method, ...extra) => {
    const fromMethods = (identity?.methods || []).flatMap((m) => [m.email, m.phone]);
    rememberLoginMethod([identity?.typed, identity?.email, identity?.phone, ...fromMethods, ...extra], method);
  };

  const submitLogin = async (email, password) => {
    setLoading(true);
    try {
      const res = await login(email, password);
      if (res && res.requiresDeviceVerification) {
        setDeviceVerify({ channel: res.channel, hint: res.hint, email: res.email, phone: res.phone });
        setDeviceCode(res.dev_code || "");
        toast.success(tr("dv_sent"));
        showDevCode(res);
        return;
      }
      saveRememberedIdentifier({ kind: "email", value: email, first_name: identity?.first_name });
      rememberMethod("password", email);
      toast.success(`${tr("welcome")} ${email} !`, { duration: 2000, position: "bottom-left" });
      nav("/home");
    } catch (e2) { toast.error(asError(e2)); }
    finally { setLoading(false); }
  };
  const doLogin = (e) => { e.preventDefault(); return submitLogin(loginData.email, loginData.password); };

  // New-device step-up verification (email account logging in from an untrusted device)
  const verifyDeviceOtp = async () => {
    if (deviceCode.trim().length !== 6) { toast.error(tr("otp_placeholder")); return; }
    setDeviceLoading(true);
    try {
      await verifyDevice({ email: deviceVerify.email, phone: deviceVerify.phone, code: deviceCode.trim() });
      saveRememberedIdentifier({ kind: "email", value: loginData.email, first_name: identity?.first_name });
      rememberMethod("password", loginData.email);
      toast.success(tr("welcome"));
      nav("/home");
    } catch (e2) { toast.error(asError(e2)); }
    finally { setDeviceLoading(false); }
  };
  const resendDeviceOtp = async () => {
    try { const r = await login(loginData.email, loginData.password); toast.success(tr("dv_sent")); if (r && r.dev_code) setDeviceCode(r.dev_code); showDevCode(r); }
    catch (e2) { toast.error(asError(e2)); }
  };

  // WhatsApp passwordless login
  const waLoginStart = async (e164 = waLoginPhone.e164) => {
    if (!e164 || e164.replace(/\D/g, "").length < 8) { toast.error(tr("wa_phone_invalid")); return; }
    const blocked = await phoneBlockMessage(e164, waLoginPhone.iso);
    if (blocked) { toast.error(blocked); return; }
    setLoading(true);
    try {
      const r = await startPhoneLogin(e164);
      setWaLoginView("otp");
      setWaLoginCode(r && r.dev_code ? r.dev_code : "");
      setWaLoginCooldown(45);
      showDevCode(r);
    } catch (e2) { toast.error(asError(e2)); }
    finally { setLoading(false); }
  };
  const waLoginVerify = async () => {
    if (waLoginCode.trim().length !== 6) { toast.error(tr("otp_placeholder")); return; }
    setLoading(true);
    try {
      await verifyPhoneLogin(waLoginPhone.e164, waLoginCode.trim());
      saveRememberedIdentifier({ kind: "phone", value: waLoginPhone.e164, first_name: identity?.first_name });
      rememberMethod("whatsapp", waLoginPhone.e164);
      toast.success(tr("welcome"));
      nav("/home");
    } catch (e2) { toast.error(asError(e2)); }
    finally { setLoading(false); }
  };
  const waLoginResend = async () => {
    if (waLoginCooldown > 0) return;
    try { const r = await startPhoneLogin(waLoginPhone.e164); setWaLoginCooldown(45); toast.message(tr("wa_resent")); if (r && r.dev_code) setWaLoginCode(r.dev_code); showDevCode(r); }
    catch (e2) { toast.error(asError(e2)); }
  };

  // Connexion unique : « Continuer » → le serveur dit si le compte existe et quelle est l'étape suivante.
  const backToIdentify = () => {
    setIdStep("identify"); setIdentity(null); setWaLoginView("form"); setWaLoginCode(""); setLoginData({ email: "", password: "" }); setFpMode(null);
    setEmLoginView("form"); setEmLoginCode(""); setEmLoginEmail("");
  };
  // Lot 563 — connexion par code e-mail.
  const emailLoginStart = async (emailOverride) => {
    const email = (typeof emailOverride === "string" ? emailOverride : emLoginEmail).trim().toLowerCase();
    if (!isValidEmail(email)) { toast.error(tr("id_invalid")); return; }
    setLoading(true);
    try {
      const r = await startEmailLogin(email);
      setEmLoginEmail(email); setEmLoginView("otp"); setEmLoginCode(r && r.dev_code ? r.dev_code : ""); setEmLoginCooldown(45);
      showDevCode(r);
    } catch (e2) { toast.error(asError(e2)); }
    finally { setLoading(false); }
  };
  const emailLoginVerify = async () => {
    if (emLoginCode.trim().length !== 6) { toast.error(tr("otp_placeholder")); return; }
    setLoading(true);
    try {
      await verifyEmailLogin(emLoginEmail, emLoginCode.trim());
      saveRememberedIdentifier({ kind: "email", value: emLoginEmail, first_name: identity?.first_name });
      rememberMethod("email_code", emLoginEmail);
      toast.success(tr("welcome"));
      nav("/home");
    } catch (e2) { toast.error(asError(e2)); }
    finally { setLoading(false); }
  };
  const emailLoginResend = async () => {
    if (emLoginCooldown > 0) return;
    try { const r = await startEmailLogin(emLoginEmail); setEmLoginCooldown(45); toast.message(tr("id_email_resent")); if (r && r.dev_code) setEmLoginCode(r.dev_code); showDevCode(r); }
    catch (e2) { toast.error(asError(e2)); }
  };
  // Choix du canal (WhatsApp / code e-mail / mot de passe) : le canal de l'identifiant TAPÉ part directement,
  // l'autre doit être ressaisi (indice masqué) — règle du serveur, voir routes_auth._login_methods.
  const chooseMethod = async (m) => {
    setIdentity((p) => ({ ...p, hint: m.hint, method: m.method }));
    if (m.method === "whatsapp") {
      if (m.phone) {
        const p = phoneFromInput(m.phone, identifierIso || waLoginDefaultIso);
        setWaLoginPhone(p); setIdStep("otp");
        await waLoginStart(p.e164);
      } else {
        setWaLoginPhone({ iso: identifierIso || waLoginDefaultIso, national: "", e164: "" }); setWaLoginView("form"); setIdStep("otp");
      }
      return;
    }
    if (m.method === "email_code") {
      setEmLoginCode("");
      if (m.email) { setEmLoginEmail(m.email); setIdStep("email_otp"); await emailLoginStart(m.email); }
      else { setEmLoginEmail(""); setEmLoginView("form"); setIdStep("email_otp"); }
      return;
    }
    setLoginData({ email: m.email || "", password: "" });
    setIdStep("password");
  };
  const otherMethod = () => { setWaLoginView("form"); setWaLoginCode(""); setEmLoginView("form"); setEmLoginCode(""); setIdStep("choose"); };
  // Lot 566 : « Oublier » → efface la mémoire du canal sur cet appareil ; l'ordre du serveur (préféré, puis WhatsApp…) reprend.
  const forgetMethodMemory = () => {
    forgetLoginMethods();
    setIdentity((p) => (p ? { ...p, methods: p.serverMethods || (p.methods || []).map(({ last_used, ...m }) => m) } : p));
    toast.message(tr("id_m_forgotten"));
  };
  // Garde-fou : compte sans mot de passe dont la connexion WhatsApp est indisponible → code e-mail → mot de passe → connecté.
  const startSetPassword = async (data) => {
    setFpMode("set"); setFpCode(""); setFpToken(""); setFpNewPass(""); setFpConfirm("");
    if (data.email) {
      setFpEmail(data.email);
      try { await forgotPassword(data.email); setForgotStep("sent"); setFpCooldown(45); }
      catch (e2) { toast.error(asError(e2)); setForgotStep("email"); }
    } else { setFpEmail(""); setForgotStep("email"); }
  };
  const identify = async (rawOverride) => {
    const raw = typeof rawOverride === "string" ? rawOverride : identifier;
    const kind = detectIdentifierKind(raw);
    let payload;
    if (kind === "email") {
      if (!isValidEmail(raw)) { toast.error(tr("id_invalid")); return; }
      payload = { identifier: raw.trim().toLowerCase(), kind };
    } else if (kind === "phone") {
      const p = phoneFromInput(raw, identifierIso || waLoginDefaultIso);
      if (!p.e164) { toast.error(tr("wa_phone_invalid")); return; }
      const blocked = await phoneBlockMessage(p.candidate, p.iso);
      if (blocked) { toast.error(blocked); return; }
      payload = { identifier: p.e164, kind };
    } else { toast.error(tr("id_invalid")); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/identify", payload);
      // Lot 564 : le canal utilisé la dernière fois sur cet appareil (pour ce compte, sinon le dernier tous comptes) passe en tête.
      const ordered = orderLoginMethods(data.methods, recallLoginMethod(payload.identifier, data.email, data.phone));
      setIdentity({ ...data, methods: ordered, serverMethods: data.methods, raw: raw.trim(), typed: payload.identifier });
      if (data.next === "register") { setIdStep("unknown"); return; }
      if (data.next === "recover") { setIdStep("recover"); return; }
      // Lot 563 : plusieurs moyens → choix du canal ; un seul → directement ; aucun `methods` (ancien serveur) → logique historique.
      const methods = Array.isArray(ordered) ? ordered : null;
      if (methods && methods.length >= 2) { setIdStep("choose"); return; }
      if (methods && methods.length === 1) { await chooseMethod(methods[0]); return; }
      if (data.next === "set_password") { await startSetPassword(data); return; }
      if (data.next === "otp" && waAuthEnabled) {
        if (data.phone) {
          const p = phoneFromInput(data.phone, identifierIso || waLoginDefaultIso);
          setWaLoginPhone(p);
          setIdStep("otp");
          await waLoginStart(p.e164);
        } else {
          setWaLoginPhone({ iso: identifierIso || waLoginDefaultIso, national: "", e164: "" });
          setWaLoginView("form");
          setIdStep("otp");
        }
        return;
      }
      setLoginData({ email: data.email || "", password: "" });
      setIdStep("password");
    } catch (e2) { toast.error(asError(e2)); }
    finally { setLoading(false); }
  };
  // Connexion mémorisée : un tap sur le dernier compte connecté ici → on repart directement à l'étape suivante.
  const useRemembered = () => { if (!remembered) return; setIdentifier(remembered.value); identify(remembered.value); };
  const forgetRemembered = () => { forgetRememberedIdentifier(); setRemembered(null); };
  // Identifiant inconnu → inscription existante, déjà pré-remplie (le parcours d'inscription ne change pas).
  const goRegisterPrefilled = () => {
    if (!identity) return;
    if (unifiedLogin) {
      // Champ unique pré-rempli et VERROUILLÉ (« Modifier » ramène à la page 1) ; numéro alors que WhatsApp est
      // désactivé → champ vide (il faudra un e-mail).
      const lock = identity.kind === "email" || (identity.kind === "phone" && waAuthEnabled);
      setRegIdentifier(lock ? identity.identifier : "");
      setRegIdentifierIso(identity.kind === "phone" ? (identifierIso || waLoginDefaultIso) : "");
      setRegLocked(lock);
    } else if (identity.kind === "phone" && waAuthEnabled) {
      setRegMethod("whatsapp");
      setRegPhone(phoneFromInput(identity.identifier, identifierIso || waLoginDefaultIso));
    } else {
      setRegMethod("email");
      if (identity.kind === "email") set("email", identity.identifier);
    }
    setWaUnavailable(false); setRegView("form"); setStep(1); setTab("register"); backToIdentify();
  };
  // « Modifier » sur l'identifiant verrouillé → retour à la page 1, champ unique pré-rempli (on recherche d'abord le compte).
  const editLockedIdentifier = () => {
    setRegLocked(false); setIdentifier(regIdentifier); setRegIdentifier(""); setTab("login"); backToIdentify();
  };
  // « Nouveau sur Yalola ? Créer un compte » : ce qui est déjà tapé suit (modifiable, pas de recherche de compte).
  const goRegisterFromIdentify = () => {
    if (unifiedLogin) { setRegIdentifier(identifier.trim()); setRegIdentifierIso(identifierIso); setRegLocked(false); }
    setTab("register"); setStep(1); setRegView("form");
  };

  const validateStep = (s) => {
    if (s === 1) {
      if (!regData.first_name.trim() || !regData.last_name.trim()) return tr("required_error");
      if (unifiedLogin) {
        if (!regKind) return tr("id_invalid");
        if (regKind === "phone") {
          if (regWaOff) return tr("reg_wa_off");
          if (!regPhoneEff.e164 || regPhoneEff.e164.replace(/\D/g, "").length < 8) return tr("wa_phone_invalid");
        } else if (!isValidEmail(regEmailEff) || regData.password.length < 6) {
          return tr("required_error");
        }
      } else if (waAuthEnabled && regMethod === "whatsapp") {
        if (!regPhone.e164 || regPhone.e164.replace(/\D/g, "").length < 8) return tr("wa_phone_invalid");
      } else if (!regData.email || regData.password.length < 6) {
        return tr("required_error");
      }
      const minA = stats?.min_age || 0;
      const maxA = stats?.max_age || 0;
      const ageRequired = reqOf("birth_date") || minA > 0 || maxA > 0;
      if (ageRequired && !regData.birth_date) return tr("required_error");
      if (regData.birth_date && (minA > 0 || maxA > 0)) {
        const age = ageFromBirth(regData.birth_date);
        if (minA > 0 && (age === null || age < minA)) return tr("age_min_error").replace("{min}", minA);
        if (maxA > 0 && age !== null && age > maxA) return tr("age_max_error").replace("{max}", maxA);
      }
      if (!acceptTerms) return tr("terms_required");
      if (lightSignup && !regData.denomination) return tr("required_error");
    }
    if (s === 2) {
      if (reqOf("denomination") && !regData.denomination) return tr("required_error");
      if (reqOf("country") && !regData.country) return tr("required_error");
      if (reqOf("city") && !regData.city.trim()) return tr("required_error");
    }
    if (s === 3) {
      if (reqOf("interests") && regData.interests.length < 3) return tr("interests_min");
    }
    return null;
  };

  const nextStep = async () => {
    const err = validateStep(step);
    if (err) { toast.error(err); return; }
    if (step === 1 && regMethodEff === "whatsapp") {
      const blocked = await phoneBlockMessage(regPhoneEff.e164, regPhoneEff.iso);
      if (blocked) { toast.error(blocked); return; }
    }
    setDraftRestored(false);
    setStep((x) => Math.min(lastStep, x + 1));
  };

  const doRegister = async () => {
    const err = validateStep(lightSignup ? 1 : 3);
    if (err) { toast.error(err); return; }
    if (ageCheck === "checking" || ageCheck === "blocked") return;
    setLoading(true);
    const ageAssurance = ageAssurancePayload(readAgeRange());
    const base = {
      ...regData,
      email: regEmailEff,
      name: `${regData.first_name.trim()} ${regData.last_name.trim()}`.trim(),
      church_name: "",
      church_leader: "",
      denomination: regData.denomination,
      language: lang,
      accept_terms: acceptTerms,
      // Formulaire court : le serveur marque le compte « light » (profil à compléter) ; genre / pays / ville / rôle /
      // centres d'intérêt ne sont PAS envoyés même si un brouillon les contenait.
      ...(lightSignup ? { signup_mode: "light", gender: null, country: null, city: "", role: "user", interests: [] } : {}),
      ...(refCode ? { referral_code: refCode } : {}),
      ...(ageAssurance ? { age_assurance: ageAssurance } : {}),
    };
    try {
      if (regMethodEff === "whatsapp" && waAuthEnabled) {
        const blocked = await phoneBlockMessage(regPhoneEff.e164, regPhoneEff.iso);
        if (blocked) { toast.error(blocked); return; }
        const payload = { ...base, phone: regPhoneEff.e164 };
        delete payload.email; delete payload.password;
        const r = await startPhoneRegistration(payload);
        // WhatsApp configuré mais Meta refuse l'envoi (indispo / rate-limité) → on reste sur le
        // formulaire et on propose le repli e-mail (champ additif ; apps déjà publiées inchangées).
        if (r && r.whatsapp_available === false) {
          setWaUnavailable(true);
          toast.error(tr("wa_unavailable"));
          return;
        }
        setWaUnavailable(false);
        setOtpPhone(regPhoneEff.e164);
        setRegOtpChannel("whatsapp");
        setOtpCode(r && r.dev_code ? r.dev_code : "");
        setRegView("otp");
        setCooldown(45);
        toast.success(tr("wa_code_sent"));
        showDevCode(r);
      } else {
        const email = regEmailEff.trim().toLowerCase();
        const r = await startRegistration({ ...base, email });
        setOtpEmail(email);
        setRegOtpChannel("email");
        setOtpCode(r && r.dev_code ? r.dev_code : "");
        setRegView("otp");
        setCooldown(45);
        toast.success(tr("otp_sent"));
        showDevCode(r);
      }
    } catch (e2) { toast.error(asError(e2)); }
    finally { setLoading(false); }
  };

  const verifyOtp = async () => {
    if (otpCode.trim().length !== 6) { toast.error(tr("otp_placeholder")); return; }
    setOtpLoading(true);
    try {
      if (regOtpChannel === "whatsapp") {
        await verifyPhoneRegistration(otpPhone, otpCode.trim());
      } else {
        await verifyRegistration(otpEmail, otpCode.trim());
      }
      toast.success(tr("account_created"));
      clearRegisterDraft();
      clearRefCode(); // lot 705 — code d'invitation consommé
      nav("/home");
    } catch (e2) { toast.error(asError(e2)); }
    finally { setOtpLoading(false); }
  };

  const resendOtp = async () => {
    if (cooldown > 0) return;
    try {
      const r = regOtpChannel === "whatsapp" ? await resendPhoneRegistration(otpPhone) : await resendRegistration(otpEmail);
      setCooldown(45);
      toast.success(regOtpChannel === "whatsapp" ? tr("wa_code_sent") : tr("otp_sent"));
      if (r && r.dev_code) setOtpCode(r.dev_code);
      showDevCode(r);
    } catch (e2) { toast.error(asError(e2)); }
  };

  // Forgot-password handlers. The server answer is deliberately neutral (no account
  // enumeration), so the UI never claims a code was sent: "if an account matches…".
  const fpSend = async () => {
    const email = fpEmail.trim().toLowerCase();
    if (!email) { toast.error(tr("email")); return; }
    setFpLoading(true);
    try { await forgotPassword(email); setFpEmail(email); setFpChannel("email"); setFpWaOpen(false); setForgotStep("sent"); setFpCode(""); setFpCooldown(45); }
    catch (e2) { toast.error(asError(e2)); }
    finally { setFpLoading(false); }
  };
  // Lot 604 — même règle anti-abus que la connexion : le numéro doit être ressaisi (et correspondre au compte).
  const fpWaHint = identity?.methods?.find((m) => m.method === "whatsapp")?.hint || null;
  const fpSendWa = async () => {
    const e164 = fpWaPhone?.e164;
    if (!e164 || e164.replace(/\D/g, "").length < 8) { toast.error(tr("wa_phone_invalid")); return; }
    setFpLoading(true);
    try {
      await forgotPassword(fpEmail.trim().toLowerCase(), { channel: "whatsapp", phone: e164, language: lang });
      setFpChannel("whatsapp"); setFpWaOpen(false); setForgotStep("otp"); setFpCode(""); setFpCooldown(45);
    } catch (e2) { toast.error(asError(e2)); }
    finally { setFpLoading(false); }
  };
  const fpWaOffer = fpWaHint && fpChannel === "email" ? (
    <div className="rounded-2xl border p-3 space-y-2" style={{ borderColor: "var(--yalola-border)" }} data-testid="forgot-wa-offer">
      <button type="button" onClick={() => setFpWaOpen((v) => !v)} className="w-full text-sm font-medium text-left flex items-center gap-2" style={{ color: "var(--yalola-primary)" }} data-testid="forgot-wa-toggle">
        <MessageCircle size={16} /> {tr("fp_wa_offer").replace("{hint}", fpWaHint)}
      </button>
      {fpWaOpen && (
        <div className="space-y-2 pt-1">
          <p className="text-xs" style={{ color: "var(--yalola-text-muted)" }}>{tr("fp_wa_desc").replace("{hint}", fpWaHint)}</p>
          <PhoneField value={fpWaPhone} onChange={setFpWaPhone} lang={lang} testId="forgot-wa-phone" defaultIso={waLoginDefaultIso} />
          <Button type="button" onClick={fpSendWa} disabled={fpLoading} className="w-full pill-primary rounded-full h-10" data-testid="forgot-wa-send">{fpLoading ? tr("fp_sending") : tr("fp_wa_send")}</Button>
        </div>
      )}
    </div>
  ) : null;
  const fpResend = async () => {
    if (fpCooldown > 0) return;
    try { await resendForgot(fpEmail); setFpCooldown(45); toast.message(tr("fp_resent")); } catch (e2) { toast.error(asError(e2)); }
  };
  const fpVerify = async () => {
    if (fpCode.trim().length !== 6) { toast.error(tr("otp_placeholder")); return; }
    setFpLoading(true);
    try { const d = await verifyForgot(fpEmail, fpCode.trim()); setFpToken(d.reset_token); setForgotStep("reset"); setFpNewPass(""); setFpConfirm(""); }
    catch (e2) { toast.error(asError(e2)); }
    finally { setFpLoading(false); }
  };
  const fpReset = async () => {
    if (fpNewPass.length < 6) { toast.error(tr("fp_pass_short")); return; }
    if (fpNewPass !== fpConfirm) { toast.error(tr("fp_no_match")); return; }
    setFpLoading(true);
    try {
      await resetPassword(fpEmail, fpToken, fpNewPass);
      const setMode = fpMode === "set";
      toast.success(setMode ? tr("id_setpw_done") : tr("fp_success"));
      setLoginData({ email: fpEmail, password: "" });
      setForgotStep(null); setFpCode(""); setFpToken(""); setFpNewPass(""); setFpConfirm(""); setFpMode(null);
      if (setMode) {
        setLoginData({ email: fpEmail, password: fpNewPass });
        setIdStep("password");
        await submitLogin(fpEmail, fpNewPass);
      }
    } catch (e2) { toast.error(asError(e2)); }
    finally { setFpLoading(false); }
  };

  const toggleInterest = (i) => set("interests", regData.interests.includes(i) ? regData.interests.filter((x) => x !== i) : [...regData.interests, i]);

  // App Store 1.2: Terms + Privacy visible before sign-in (e-mail and WhatsApp forms).
  const legalLink = (href, label, testId) => (
    <a href={withLang(href, lang)} target="_blank" rel="noreferrer" className="font-semibold underline inline-block py-1 px-0.5 -my-1" style={{ color: "var(--yalola-primary)" }} data-testid={testId}>{label}</a>
  );
  const LoginLegalNote = ({ id }) => consentDone ? null : (
    <p className="text-[11px] leading-relaxed text-center" style={{ color: "var(--yalola-text-muted)" }} data-testid={id}>
      {tr("login_legal_prefix")} {legalLink("/legal/terms", tr("legal_terms"), `${id}-terms`)} {tr("accept_and")} {legalLink("/legal/privacy", tr("legal_privacy_full"), `${id}-privacy`)}.
    </p>
  );

  const StepDots = () => lightSignup ? null : (
    <div className="flex items-center justify-center gap-2 mb-4" data-testid="reg-steps">
      {[1, 2, 3].map((s) => (
        <React.Fragment key={s}>
          <div
            className="h-7 w-7 rounded-full grid place-items-center text-xs font-bold transition-colors"
            style={{
              background: s <= step ? "var(--yalola-primary)" : "var(--yalola-surface-alt)",
              color: s <= step ? "#fff" : "var(--yalola-text-muted)",
            }}
            data-testid={`reg-step-dot-${s}`}
          >
            {s}
          </div>
          {s < 3 && <div className="h-[3px] w-8 rounded-full" style={{ background: s < step ? "var(--yalola-primary)" : "var(--yalola-border)" }} />}
        </React.Fragment>
      ))}
    </div>
  );

  const demoHint = (() => {
    const raw = rawAuth("demo");
    const demoText = (raw !== undefined && raw !== null) ? raw : tr("demo");
    return demoText ? <p className="text-xs text-center" style={{ color: "var(--yalola-text-muted)" }} data-testid="login-demo-hint">{demoText}</p> : null;
  })();
  // Interrupteur admin (Réglages › Inscription & connexion) : connexion unique (un seul champ) ou anciennes
  // pages (choix E-mail / WhatsApp). Lu à chaque chargement via /config/public → retour arrière sans mise à jour.
  // (`unifiedLogin` est déclaré plus haut, à côté de waAuthEnabled.)
  const waOtpBlock = (
    <div className="space-y-4" data-testid="wa-login-otp">
      <div className="text-center">
        <div className="h-16 w-16 mx-auto rounded-full grid place-items-center" style={{ background: "var(--yalola-primary-soft)" }}><MessageCircle size={30} style={{ color: "var(--yalola-primary)" }} /></div>
        <h3 className="mt-3 text-xl font-bold font-display">{tr("wa_otp_title")}</h3>
        <p className="mt-1 text-sm" style={{ color: "var(--yalola-text-muted)" }} data-testid="wa-login-otp-desc">{(unifiedLogin && identity?.exists ? tr("id_otp_sent") : tr("wa_login_otp_desc")).replace("{phone}", waLoginPhone.e164)}</p>
      </div>
      <Input inputMode="numeric" maxLength={6} value={waLoginCode} onChange={(e) => setWaLoginCode(e.target.value.replace(/\D/g, ""))} placeholder={tr("otp_placeholder")} className="text-center text-3xl tracking-[0.5em] font-bold h-16" data-testid="wa-login-code" />
      <Button onClick={waLoginVerify} disabled={loading} className="w-full pill-primary rounded-full h-11" data-testid="wa-login-verify">{loading ? tr("otp_verifying") : tr("wa_verify_login")}</Button>
      <div className="flex items-center justify-between">
        <button type="button" onClick={() => { setWaLoginView("form"); setWaLoginCode(""); }} className="text-sm" style={{ color: "var(--yalola-text-muted)" }} data-testid="wa-login-change">{tr("wa_change_number")}</button>
        <button type="button" onClick={waLoginResend} disabled={waLoginCooldown > 0} className="text-sm font-medium" style={{ color: waLoginCooldown > 0 ? "var(--yalola-text-muted)" : "var(--yalola-primary)" }} data-testid="wa-login-resend">{waLoginCooldown > 0 ? tr("otp_resend_in").replace("{s}", waLoginCooldown) : tr("otp_resend")}</button>
      </div>
    </div>
  );
  const renderWaForm = (legacy) => (
    <div className="space-y-4" data-testid="wa-login-form">
      <div className="text-center">
        <div className="h-14 w-14 mx-auto rounded-full grid place-items-center" style={{ background: "var(--yalola-primary-soft)" }}><MessageCircle size={24} style={{ color: "var(--yalola-primary)" }} /></div>
        <h3 className="mt-2 font-bold font-display text-lg">{tr("wa_login_title")}</h3>
        <p className="text-sm" style={{ color: "var(--yalola-text-muted)" }} data-testid="wa-login-form-desc">{!legacy && identity?.hint ? tr("id_otp_hint").replace("{hint}", identity.hint) : tr("wa_login_desc")}</p>
      </div>
      <div>
        <Label>{tr("wa_phone_label")}</Label>
        <PhoneField value={waLoginPhone} onChange={setWaLoginPhone} lang={lang} testId="wa-login-phone" defaultIso={waLoginDefaultIso} onCheck={setWaCheck} />
      </div>
      <Button onClick={() => waLoginStart()} disabled={loading || isBlockingCheck(waCheck)} className="w-full pill-primary rounded-full h-11" data-testid="wa-login-send">{loading ? tr("fp_sending") : tr("wa_send_code")}</Button>
      <LoginLegalNote id="wa-login-legal-note" />
      {legacy && (
        <Link to="/recuperer-mon-compte" className="block w-full text-center text-xs underline" style={{ color: "var(--yalola-text-muted)" }} data-testid="recover-account-link-wa">
          {tr("recover_link")}
        </Link>
      )}
    </div>
  );
  const renderPasswordForm = (legacy) => (
    <form onSubmit={doLogin} className="space-y-4">
      {(legacy || !identity?.email) && (
        <div>
          <Label>{tr("email")}</Label>
          {!legacy && identity?.hint ? <p className="text-xs mb-1" style={{ color: "var(--yalola-text-muted)" }} data-testid="login-pw-hint">{tr("id_pw_hint").replace("{hint}", identity.hint)}</p> : null}
          <Input type="email" required value={loginData.email} onChange={(e) => setLoginData({ ...loginData, email: e.target.value })} data-testid="login-email" />
        </div>
      )}
      <div>
        <Label>{tr("password")}</Label>
        <PasswordInput autoFocus={!legacy} required value={loginData.password} onChange={(e) => setLoginData({ ...loginData, password: e.target.value })} data-testid="login-password" />
      </div>
      <Button type="submit" disabled={loading} className="w-full pill-primary rounded-full h-11" data-testid="login-submit">
        {loading ? tr("logging_in") : at("login_button", tr("login"))}
      </Button>
      <LoginLegalNote id="login-legal-note" />
      <button type="button" onClick={() => { setForgotStep("email"); setFpEmail(loginData.email || ""); }} className="w-full text-sm font-medium" style={{ color: "var(--yalola-primary)" }} data-testid="forgot-password-link">
        {tr("forgot_link")}
      </button>
      <Link to="/recuperer-mon-compte" className="block w-full text-center text-xs underline" style={{ color: "var(--yalola-text-muted)" }} data-testid="recover-account-link">
        {tr("recover_link")}
      </Link>
      {legacy && demoHint}
    </form>
  );
  const legacyLoginBlock = (
    <>
      {waAuthEnabled && (
        <div className="grid grid-cols-2 gap-2 p-1 rounded-full" style={{ background: "var(--yalola-surface-alt)" }} data-testid="login-method-toggle">
          {loginMethodOrder.map((m) => { const [label, Icon] = methodMeta[m]; return (
            <button key={m} type="button" onClick={() => setLoginMethod(m)} className="flex items-center justify-center gap-1.5 h-9 rounded-full text-sm font-semibold transition-colors" style={{ background: loginMethod === m ? "white" : "transparent", color: loginMethod === m ? "var(--yalola-primary)" : "var(--yalola-text-muted)", boxShadow: loginMethod === m ? "0 1px 3px rgba(0,0,0,0.08)" : "none" }} data-testid={`login-method-${m}`}>
              <Icon size={15} /> {label}
            </button>
          ); })}
        </div>
      )}
      {waAuthEnabled && loginMethod === "whatsapp" ? (waLoginView === "otp" ? waOtpBlock : renderWaForm(true)) : renderPasswordForm(true)}
    </>
  );

  return (
    <div className="min-h-[100dvh] relative overflow-hidden flex flex-col" style={{ background: "var(--yalola-bg)", paddingTop: "var(--sa-top)", paddingBottom: "var(--sa-bottom)" }}>
      <StatusBarShim />
      <NavBarBackdrop />
      <div className="absolute inset-0 opacity-40 pointer-events-none" aria-hidden>
        <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full blur-3xl" style={{ background: "#F2C46D66" }} />
        <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full blur-3xl" style={{ background: "#D9775766" }} />
        <div className="absolute top-1/3 right-1/4 w-72 h-72 rounded-full blur-3xl" style={{ background: "#2D5A4C33" }} />
      </div>

      <div className="relative flex-1 flex items-center justify-center px-4 py-5 sm:py-8">
        <div className="max-w-6xl w-full grid lg:grid-cols-2 gap-6 lg:gap-10 items-center">
          {/* Hero side */}
          <div className="text-center lg:text-left onb-fade-up onb-delay-1">
            <div className="flex items-center gap-3 justify-center lg:justify-start">
              <YalolaLogo size={56} />
              <h1 className="text-5xl lg:text-6xl font-extrabold tracking-tight" style={{ fontFamily: "Outfit", color: "var(--yalola-text)" }}>{config?.settings?.app_name || "Yalola"}</h1>
            </div>
            <p className="mt-3 text-lg lg:text-2xl" style={{ color: "var(--yalola-secondary)", fontFamily: "Outfit", fontWeight: 500 }}>{at("title", tr("subtitle"))}</p>
            <p className="mt-3 lg:mt-6 text-sm lg:text-base leading-relaxed max-w-lg mx-auto lg:mx-0 hidden md:block" style={{ color: "var(--yalola-text-muted)" }}>{at("subtitle", tr("desc"))}</p>
            <div className="mt-4 lg:mt-8 hidden md:flex flex-wrap gap-2 justify-center lg:justify-start">
              {[
                { icon: Church, label: tr("chip_church") },
                { icon: HeartHandshake, label: tr("chip_match") },
                { icon: Radio, label: tr("chip_live") },
                { icon: Users, label: tr("chip_groups") },
              ].map(({ icon: Icon, label }, i) => (
                <span key={label} className="chip onb-chip" style={{ animationDelay: `${0.5 + i * 0.13}s` }}><Icon size={14} /> {label}</span>
              ))}
            </div>

            {stats && config?.settings?.show_landing_stats && (
              <div className="mt-6 flex items-center gap-3 justify-center lg:justify-start" data-testid="member-counter">
                <div className="flex -space-x-2" aria-hidden>
                  {["#D97757", "#1A7B5C", "#E0A63C"].map((c) => (
                    <span key={c} className="h-8 w-8 rounded-full border-2 border-white grid place-items-center" style={{ background: c }}>
                      <Users size={14} className="text-white" />
                    </span>
                  ))}
                </div>
                <span className="text-sm font-semibold" style={{ color: "var(--yalola-text)" }} data-testid="member-counter-text">
                  {stats.total_users > 0
                    ? tr("join_members").replace("{count}", stats.total_users.toLocaleString(lang === "fr" ? "fr-FR" : lang))
                    : tr("join_first")}
                </span>
              </div>
            )}
          </div>

          {/* Form side */}
          <div className="yalola-card p-6 lg:p-8 w-full max-w-md mx-auto lg:max-w-none onb-fade-up onb-delay-2" data-testid="onboarding-card">
            {/* lot 767 — bannière de bienvenue d'un pays « relancé » (visiteurs non connectés) */}
            <WelcomeCountryBanner lang={lang} onRegister={() => { setTab("register"); setStep(1); setRegView("form"); }} />
            {/* Language selector — big visible flags, same page as login (web) ; app native : la langue vient de « Bienvenue »,
                le même sélecteur compact reste disponible pour la changer. */}
            {consentDone && (
              <div className="flex justify-end -mt-2 mb-2" data-testid="onb-lang-compact">
                <LangFlags testId="onb-compact" size={20} />
              </div>
            )}
            {!consentDone && <div className="mb-5">
              <div className="text-sm font-semibold mb-2" style={{ color: "var(--yalola-text-muted)" }}>{tr("choose_lang")}</div>
              <div className="grid grid-cols-4 gap-2" data-testid="onb-lang-row">
                {LANGUAGES.map((l) => (
                  <button
                    key={l.code}
                    type="button"
                    onClick={() => setLang(l.code)}
                    className="flex flex-col items-center gap-1 py-2 rounded-xl border-2 transition-all"
                    style={{
                      borderColor: lang === l.code ? "var(--yalola-primary)" : "var(--yalola-border)",
                      background: lang === l.code ? "var(--yalola-primary-soft)" : "var(--yalola-surface)",
                    }}
                    data-testid={`onb-lang-${l.code}`}
                  >
                    <Flag code={l.code} size={22} />
                    <span className="text-[11px] font-bold" style={{ color: lang === l.code ? "var(--yalola-primary)" : "var(--yalola-text-muted)" }}>{l.code.toUpperCase()}</span>
                  </button>
                ))}
              </div>
            </div>}

            <Tabs value={tab} onValueChange={(v) => { setTab(v); setStep(1); setRegView("form"); setForgotStep(null); setDeviceVerify(null); setWaLoginView("form"); backToIdentify(); }}>
              <TabsList className="grid grid-cols-2 w-full rounded-full p-1">
                <TabsTrigger value="login" className="rounded-full" data-testid="tab-login">{tr("login")}</TabsTrigger>
                <TabsTrigger value="register" className="rounded-full" data-testid="tab-register">{tr("register")}</TabsTrigger>
              </TabsList>
              {consentDone && (
                <p className="mt-3 flex items-start gap-2 text-xs leading-relaxed" style={{ color: "var(--yalola-text-muted)" }} data-testid="onboarding-help">
                  <Info size={14} className="shrink-0 mt-[1px]" style={{ color: "var(--yalola-primary)" }} aria-hidden />
                  <span>{tab === "register" ? registerHelp : t("onboarding.login_help", tr("login_help"))}</span>
                </p>
              )}

              <TabsContent value="login">
                {deviceVerify ? (
                  <div className="mt-4 space-y-4" data-testid="device-verify">
                    <div className="text-center">
                      <div className="h-16 w-16 mx-auto rounded-full grid place-items-center" style={{ background: "var(--yalola-primary-soft)" }}><ShieldCheck size={30} style={{ color: "var(--yalola-primary)" }} /></div>
                      <h3 className="mt-3 text-xl font-bold font-display">{tr("dv_title")}</h3>
                      <p className="mt-1 text-sm" style={{ color: "var(--yalola-text-muted)" }}>{deviceVerify.channel === "whatsapp" ? tr("dv_desc_whatsapp") : tr("dv_desc_email")} <b style={{ color: "var(--yalola-text)" }}>{deviceVerify.hint}</b></p>
                    </div>
                    <Input inputMode="numeric" maxLength={6} value={deviceCode} onChange={(e) => setDeviceCode(e.target.value.replace(/\D/g, ""))} placeholder={tr("otp_placeholder")} className="text-center text-3xl tracking-[0.5em] font-bold h-16" data-testid="device-verify-code" />
                    <Button onClick={verifyDeviceOtp} disabled={deviceLoading} className="w-full pill-primary rounded-full h-11" data-testid="device-verify-submit">{deviceLoading ? tr("otp_verifying") : tr("dv_verify")}</Button>
                    <div className="flex items-center justify-between">
                      <button type="button" onClick={() => { setDeviceVerify(null); setDeviceCode(""); }} className="text-sm" style={{ color: "var(--yalola-text-muted)" }} data-testid="device-verify-cancel">{tr("fp_back_login")}</button>
                      <button type="button" onClick={resendDeviceOtp} className="text-sm font-medium" style={{ color: "var(--yalola-primary)" }} data-testid="device-verify-resend">{tr("otp_resend")}</button>
                    </div>
                  </div>
                ) : !forgotStep ? (
                  <div className="mt-4 space-y-4">
                    {!unifiedLogin && legacyLoginBlock}
                    {unifiedLogin && idStep === "identify" && (
                      <div className="space-y-4" data-testid="login-identify">
                        {remembered && !identifier && (
                          <div className="space-y-2" data-testid="login-remembered">
                            <button type="button" onClick={useRemembered} disabled={loading} className="w-full min-w-0 max-w-full overflow-hidden flex items-center gap-3 rounded-2xl border p-3 text-left transition-colors hover:bg-[color:var(--yalola-surface-alt)]" style={{ borderColor: "var(--yalola-border)", background: "var(--yalola-surface)" }} data-testid="login-remembered-continue">
                              <div className="h-10 w-10 rounded-full grid place-items-center shrink-0 font-bold" style={{ background: "var(--yalola-primary-soft)", color: "var(--yalola-primary)" }}>{(remembered.first_name || remembered.value).slice(0, 1).toUpperCase()}</div>
                              <div className="min-w-0 flex-1">
                                <div className="text-sm font-semibold line-clamp-1 break-all" data-testid="login-remembered-title">{tr("id_remembered_title").replace("{name}", remembered.first_name || remembered.value)}</div>
                                <div className="text-xs line-clamp-1 break-all" style={{ color: "var(--yalola-text-muted)" }} data-testid="login-remembered-value">{remembered.first_name ? `${remembered.value} · ` : ""}{tr("id_remembered_hint")}</div>
                              </div>
                              <ChevronRight size={18} className="shrink-0" style={{ color: "var(--yalola-primary)" }} />
                            </button>
                            <button type="button" onClick={forgetRemembered} className="w-full inline-flex items-center justify-center gap-1 text-xs" style={{ color: "var(--yalola-text-muted)" }} data-testid="login-remembered-forget"><X size={12} /> {tr("id_remembered_forget")}</button>
                            <div className="flex items-center gap-3 text-[11px] uppercase tracking-wide" style={{ color: "var(--yalola-text-muted)" }}><span className="h-px flex-1" style={{ background: "var(--yalola-border)" }} />{tr("id_or")}<span className="h-px flex-1" style={{ background: "var(--yalola-border)" }} /></div>
                          </div>
                        )}
                        <div>
                          <Label>{tr("id_label")}</Label>
                          <IdentifierField
                            value={identifier} iso={identifierIso || waLoginDefaultIso} onChange={setIdentifier} onIsoChange={setIdentifierIso} lang={lang} onSubmit={identify}
                            labels={{ placeholder: tr("id_placeholder"), hint: tr("id_hint"), email: tr("id_tag_email"), whatsapp: tr("id_tag_wa") }}
                            onCheck={setIdCheck}
                          />
                        </div>
                        <Button onClick={() => identify()} disabled={loading || !identifier.trim() || isBlockingCheck(idCheck)} className="w-full pill-primary rounded-full h-11" data-testid="login-continue">
                          {loading ? tr("id_checking") : tr("id_continue")}
                        </Button>
                        <LoginLegalNote id="login-legal-note" />
                        <p className="text-sm text-center" style={{ color: "var(--yalola-text-muted)" }}>
                          {tr("id_new")} <button type="button" onClick={goRegisterFromIdentify} className="font-semibold" style={{ color: "var(--yalola-primary)" }} data-testid="login-go-register">{tr("register")}</button>
                        </p>
                        <Link to="/recuperer-mon-compte" className="block w-full text-center text-xs underline" style={{ color: "var(--yalola-text-muted)" }} data-testid="recover-account-link">
                          {tr("recover_link")}
                        </Link>
                        {demoHint}
                      </div>
                    )}
                    {unifiedLogin && idStep === "unknown" && identity && (
                      <div className="space-y-4" data-testid="login-unknown">
                        <div className="text-center">
                          <div className="h-14 w-14 mx-auto rounded-full grid place-items-center" style={{ background: "var(--yalola-primary-soft)" }}><UserPlus size={24} style={{ color: "var(--yalola-primary)" }} /></div>
                          <h3 className="mt-2 font-bold font-display text-lg">{tr("id_unknown_title")}</h3>
                          <p className="text-sm" style={{ color: "var(--yalola-text-muted)" }}>{identity.kind === "phone" ? tr("id_unknown_phone") : tr("id_unknown_email")}</p>
                        </div>
                        <div className="rounded-2xl border p-3 text-sm leading-snug" style={{ background: "#FFF7ED", borderColor: "#FED7AA", color: "#9A3412" }}>
                          <b className="block mb-0.5 break-all" style={{ color: "#7C2D12" }} data-testid="login-unknown-identifier">{identity.identifier}</b>
                          {tr("id_unknown_help")}
                        </div>
                        <Button onClick={goRegisterPrefilled} className="w-full pill-primary rounded-full h-11" data-testid="login-register-prefilled">
                          {identity.kind === "phone" ? (waAuthEnabled ? tr("id_register_phone") : tr("register")) : tr("id_register_email")}
                        </Button>
                        <Button type="button" variant="outline" onClick={backToIdentify} className="w-full rounded-full h-11" data-testid="login-retry-identifier">{tr("id_retry")}</Button>
                        <Link to="/recuperer-mon-compte" className="block w-full text-center text-xs underline" style={{ color: "var(--yalola-text-muted)" }} data-testid="login-unknown-recover-link">
                          {tr("id_unknown_recover")}
                        </Link>
                      </div>
                    )}
                    {unifiedLogin && idStep === "recover" && identity && (
                      <div className="space-y-4" data-testid="login-recover">
                        <div className="text-center">
                          <div className="h-14 w-14 mx-auto rounded-full grid place-items-center" style={{ background: "#FEF3C7" }}><ShieldAlert size={24} style={{ color: "#B45309" }} /></div>
                          <h3 className="mt-2 font-bold font-display text-lg">{tr("id_recover_title")}</h3>
                          <p className="text-sm" style={{ color: "var(--yalola-text-muted)" }} data-testid="login-recover-desc">{tr("id_recover_desc")}</p>
                        </div>
                        <div className="rounded-2xl border p-3 text-sm leading-snug" style={{ background: "#FFF7ED", borderColor: "#FED7AA", color: "#9A3412" }}>
                          <b className="block break-all" style={{ color: "#7C2D12" }} data-testid="login-recover-identifier">{identity.first_name ? `${identity.first_name} · ` : ""}{identity.raw}</b>
                        </div>
                        <Button asChild className="w-full pill-primary rounded-full h-11">
                          <Link to="/recuperer-mon-compte" data-testid="login-recover-cta">{tr("id_recover_cta")}</Link>
                        </Button>
                        <Button type="button" variant="outline" onClick={backToIdentify} className="w-full rounded-full h-11" data-testid="login-recover-retry">{tr("id_retry")}</Button>
                      </div>
                    )}
                    {unifiedLogin && (idStep === "password" || idStep === "otp" || idStep === "choose" || idStep === "email_otp") && (
                      <div className="flex items-center gap-3 rounded-2xl border p-3" style={{ borderColor: "var(--yalola-border)", background: "var(--yalola-surface)" }} data-testid="login-identity-card">
                        <div className="h-9 w-9 rounded-full grid place-items-center shrink-0" style={{ background: "var(--yalola-primary-soft)", color: "var(--yalola-primary)" }}><UserRound size={18} /></div>
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-semibold line-clamp-1 break-all" data-testid="login-identity-title">{identity?.first_name ? `${tr("id_welcome_back")}, ${identity.first_name}` : tr("id_welcome_back")}</div>
                          <div className="text-xs line-clamp-1 break-all" style={{ color: "var(--yalola-text-muted)" }} data-testid="login-identity-value">{identity?.email || identity?.phone || identity?.raw} · {tr("id_found")}</div>
                        </div>
                        <button type="button" onClick={backToIdentify} className="text-xs font-semibold shrink-0" style={{ color: "var(--yalola-primary)" }} data-testid="login-change-identifier">{tr("id_change")}</button>
                      </div>
                    )}
                    {unifiedLogin && idStep === "choose" && identity && (
                      <div className="space-y-2" data-testid="login-methods" data-count={(identity.methods || []).length}>
                        <p className="text-sm font-semibold text-center" data-testid="login-methods-title">{tr("id_choose_title")}</p>
                        {(identity.methods || []).map((m) => {
                          const meta = {
                            whatsapp: [MessageCircle, tr("id_m_whatsapp"), m.hint, { background: "#DCFCE7", color: "#15803D" }],
                            email_code: [MailCheck, tr("id_m_email_code"), m.hint, { background: "#EFF6FF", color: "#1D4ED8" }],
                            password: [KeyRound, tr("id_m_password"), tr("id_m_password_sub").replace("{hint}", m.hint || ""), { background: "var(--yalola-primary-soft)", color: "var(--yalola-primary)" }],
                          }[m.method];
                          if (!meta) return null;
                          const [Icon, label, sub, style] = meta;
                          return (
                            <button key={m.method} type="button" onClick={() => chooseMethod(m)} disabled={loading} className="w-full flex items-center gap-3 rounded-2xl border p-3 text-left transition-colors hover:bg-[color:var(--yalola-surface-alt)]" style={{ borderColor: (m.last_used || m.preferred) ? "var(--yalola-primary)" : "var(--yalola-border)", background: "var(--yalola-surface)" }} data-testid={`login-method-${m.method}`} data-preferred={m.preferred ? "1" : "0"} data-last-used={m.last_used ? "1" : "0"}>
                              <div className="h-10 w-10 rounded-full grid place-items-center shrink-0" style={style}><Icon size={18} /></div>
                              <div className="min-w-0 flex-1">
                                <div className="text-sm font-semibold flex items-center gap-2 flex-wrap">
                                  {label}
                                  {m.last_used && <span className="chip chip-accent text-[10px] px-2 py-0" data-testid="login-method-last-used-badge">{tr("id_m_last_used")}</span>}
                                  {m.preferred && <span className="chip chip-primary text-[10px] px-2 py-0" data-testid="login-method-preferred-badge">{tr("id_m_preferred")}</span>}
                                </div>
                                <div className="text-xs break-all" style={{ color: "var(--yalola-text-muted)" }}>{sub}</div>
                              </div>
                              <ChevronRight size={18} className="shrink-0" style={{ color: "var(--yalola-primary)" }} />
                            </button>
                          );
                        })}
                        {(identity.methods || []).some((m) => m.last_used) && (
                          <button type="button" onClick={forgetMethodMemory} className="w-full inline-flex items-center justify-center gap-1 text-xs" style={{ color: "var(--yalola-text-muted)" }} data-testid="login-methods-forget"><X size={12} /> {tr("id_m_forget")}</button>
                        )}
                        <LoginLegalNote id="login-methods-legal-note" />
                      </div>
                    )}
                    {unifiedLogin && idStep === "email_otp" && (emLoginView === "otp" ? (
                      <div className="space-y-4" data-testid="email-login-otp">
                        <div className="text-center">
                          <div className="h-16 w-16 mx-auto rounded-full grid place-items-center" style={{ background: "var(--yalola-primary-soft)" }}><MailCheck size={30} style={{ color: "var(--yalola-primary)" }} /></div>
                          <h3 className="mt-3 text-xl font-bold font-display">{tr("id_email_otp_title")}</h3>
                          <p className="mt-1 text-sm" style={{ color: "var(--yalola-text-muted)" }} data-testid="email-login-otp-desc">{tr("id_email_otp_sent").replace("{email}", emLoginEmail)}</p>
                        </div>
                        <Input inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={emLoginCode} onChange={(e) => setEmLoginCode(e.target.value.replace(/\D/g, ""))} placeholder={tr("otp_placeholder")} className="text-center text-3xl tracking-[0.5em] font-bold h-16" data-testid="email-login-code" />
                        <Button onClick={emailLoginVerify} disabled={loading} className="w-full pill-primary rounded-full h-11" data-testid="email-login-verify">{loading ? tr("otp_verifying") : tr("id_email_verify")}</Button>
                        <div className="flex items-center justify-between">
                          <button type="button" onClick={() => { setEmLoginView("form"); setEmLoginCode(""); }} className="text-sm" style={{ color: "var(--yalola-text-muted)" }} data-testid="email-login-change">{tr("fp_change_email")}</button>
                          <button type="button" onClick={emailLoginResend} disabled={emLoginCooldown > 0} className="text-sm font-medium" style={{ color: emLoginCooldown > 0 ? "var(--yalola-text-muted)" : "var(--yalola-primary)" }} data-testid="email-login-resend">{emLoginCooldown > 0 ? tr("otp_resend_in").replace("{s}", emLoginCooldown) : tr("otp_resend")}</button>
                        </div>
                        <p className="text-[11px] text-center" style={{ color: "var(--yalola-text-muted)" }}>{tr("otp_check_spam")}</p>
                      </div>
                    ) : (
                      <div className="space-y-4" data-testid="email-login-form">
                        <div className="text-center">
                          <div className="h-14 w-14 mx-auto rounded-full grid place-items-center" style={{ background: "var(--yalola-primary-soft)" }}><Mail size={24} style={{ color: "var(--yalola-primary)" }} /></div>
                          <h3 className="mt-2 font-bold font-display text-lg">{tr("id_m_email_code")}</h3>
                          <p className="text-sm" style={{ color: "var(--yalola-text-muted)" }} data-testid="email-login-form-desc">{tr("id_email_form_desc").replace("{hint}", identity?.hint || "")}</p>
                        </div>
                        <div>
                          <Label>{tr("email")}</Label>
                          <Input type="email" autoComplete="username" value={emLoginEmail} onChange={(e) => setEmLoginEmail(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); emailLoginStart(); } }} data-testid="email-login-email" />
                        </div>
                        <Button onClick={() => emailLoginStart()} disabled={loading || !emLoginEmail.trim()} className="w-full pill-primary rounded-full h-11" data-testid="email-login-send">{loading ? tr("fp_sending") : tr("id_email_send")}</Button>
                        <LoginLegalNote id="email-login-legal-note" />
                      </div>
                    ))}
                    {unifiedLogin && idStep === "otp" && (waLoginView === "otp" ? waOtpBlock : renderWaForm(false))}
                    {unifiedLogin && idStep === "password" && renderPasswordForm(false)}
                    {unifiedLogin && (idStep === "otp" || idStep === "password" || idStep === "email_otp") && (identity?.methods || []).length >= 2 && (
                      <button type="button" onClick={otherMethod} className="w-full text-sm font-medium" style={{ color: "var(--yalola-primary)" }} data-testid="login-other-method">{tr("id_other_method")}</button>
                    )}
                  </div>
                ) : (
                  <div className="mt-4 space-y-4" data-testid="forgot-flow" data-mode={fpMode || "forgot"}>
                    <button type="button" onClick={() => { setForgotStep(null); setFpMode(null); }} className="text-sm flex items-center gap-1" style={{ color: "var(--yalola-text-muted)" }} data-testid="forgot-back-login">
                      <ChevronLeft size={15} /> {tr("fp_back_login")}
                    </button>
                    {forgotStep === "email" && (
                      <div className="space-y-3">
                        <div className="text-center">
                          <div className="h-14 w-14 mx-auto rounded-full grid place-items-center" style={{ background: "var(--yalola-primary-soft)" }}><KeyRound size={24} style={{ color: "var(--yalola-primary)" }} /></div>
                          <h3 className="mt-3 font-bold font-display text-lg">{fpMode === "set" ? tr("id_setpw_title") : tr("fp_title")}</h3>
                          <p className="text-sm" style={{ color: "var(--yalola-text-muted)" }} data-testid="forgot-email-desc">{fpMode === "set" ? tr("id_setpw_email_desc").replace("{hint}", identity?.hint || "") : tr("fp_email_desc")}</p>
                        </div>
                        <div><Label>{tr("email")}</Label><Input type="email" value={fpEmail} onChange={(e) => setFpEmail(e.target.value)} data-testid="forgot-email" /></div>
                        <Button onClick={fpSend} disabled={fpLoading} className="w-full pill-primary rounded-full h-11" data-testid="forgot-send">{fpLoading ? tr("fp_sending") : tr("fp_send")}</Button>
                      </div>
                    )}
                    {forgotStep === "sent" && (
                      <div className="space-y-3" data-testid="forgot-sent">
                        <div className="text-center">
                          <div className="h-14 w-14 mx-auto rounded-full grid place-items-center" style={{ background: "var(--yalola-primary-soft)" }}><MailCheck size={24} style={{ color: "var(--yalola-primary)" }} /></div>
                          <h3 className="mt-3 font-bold font-display text-lg">{fpMode === "set" ? tr("id_setpw_title") : tr("fp_sent_title")}</h3>
                          <p className="text-sm" style={{ color: "var(--yalola-text-muted)" }} data-testid="forgot-sent-desc">{(fpMode === "set" ? tr("id_setpw_desc") : tr("fp_sent_desc")).replace("{email}", fpEmail)}</p>
                        </div>
                        <Button onClick={() => setForgotStep("otp")} className="w-full pill-primary rounded-full h-11" data-testid="forgot-have-code">{tr("fp_have_code")}</Button>
                        {fpWaOffer}
                        <div className="flex items-center justify-between">
                          <button type="button" onClick={() => setForgotStep("email")} className="text-sm" style={{ color: "var(--yalola-text-muted)" }} data-testid="forgot-change-email">{tr("fp_change_email")}</button>
                          <button type="button" onClick={fpResend} disabled={fpCooldown > 0} className="text-sm font-medium" style={{ color: fpCooldown > 0 ? "var(--yalola-text-muted)" : "var(--yalola-primary)" }} data-testid="forgot-resend-sent">{fpCooldown > 0 ? tr("otp_resend_in").replace("{s}", fpCooldown) : tr("otp_resend")}</button>
                        </div>
                      </div>
                    )}
                    {forgotStep === "otp" && (
                      <div className="space-y-3">
                        <div className="text-center">
                          <div className="h-14 w-14 mx-auto rounded-full grid place-items-center" style={{ background: "var(--yalola-primary-soft)" }}><MailCheck size={24} style={{ color: "var(--yalola-primary)" }} /></div>
                          <h3 className="mt-3 font-bold font-display text-lg">{fpChannel === "whatsapp" ? tr("fp_wa_title") : tr("fp_code_title")}</h3>
                          <p className="text-sm" style={{ color: "var(--yalola-text-muted)" }} data-testid="forgot-code-desc" data-channel={fpChannel}>
                            {fpChannel === "whatsapp" ? tr("fp_wa_sent_desc").replace("{hint}", fpWaHint || "") : <>{tr("fp_code_desc")} <b style={{ color: "var(--yalola-text)" }}>{fpEmail}</b></>}
                          </p>
                        </div>
                        <Input inputMode="numeric" autoComplete="one-time-code" maxLength={6} value={fpCode} onChange={(e) => setFpCode(e.target.value.replace(/\D/g, ""))} placeholder={tr("otp_placeholder")} className="text-center text-2xl tracking-[0.5em] font-bold h-14" data-testid="forgot-code" />
                        <Button onClick={fpVerify} disabled={fpLoading} className="w-full pill-primary rounded-full h-11" data-testid="forgot-verify">{fpLoading ? tr("otp_verifying") : tr("next")}</Button>
                        {fpChannel === "email" && <p className="text-xs text-center" style={{ color: "var(--yalola-text-muted)" }} data-testid="forgot-nothing-hint">{tr("fp_nothing")}</p>}
                        {fpWaOffer}
                        {fpChannel === "whatsapp" && (
                          <button type="button" onClick={fpSend} disabled={fpLoading} className="w-full text-sm" style={{ color: "var(--yalola-text-muted)" }} data-testid="forgot-wa-back-email">{tr("fp_wa_back_email")}</button>
                        )}
                        <div className="flex items-center justify-between">
                          <button type="button" onClick={() => setForgotStep("email")} className="text-sm" style={{ color: "var(--yalola-text-muted)" }} data-testid="forgot-change-email-otp">{tr("fp_change_email")}</button>
                          <button type="button" onClick={fpResend} disabled={fpCooldown > 0} className="text-sm font-medium" style={{ color: fpCooldown > 0 ? "var(--yalola-text-muted)" : "var(--yalola-primary)" }} data-testid="forgot-resend">{fpCooldown > 0 ? tr("otp_resend_in").replace("{s}", fpCooldown) : tr("otp_resend")}</button>
                        </div>
                      </div>
                    )}
                    {forgotStep === "reset" && (
                      <div className="space-y-3">
                        <h3 className="font-bold font-display text-lg text-center">{tr("fp_new_pass")}</h3>
                        <div><Label>{tr("fp_new_pass")}</Label><PasswordInput value={fpNewPass} onChange={(e) => setFpNewPass(e.target.value)} data-testid="forgot-newpass" /></div>
                        <div><Label>{tr("fp_confirm_pass")}</Label><PasswordInput value={fpConfirm} onChange={(e) => setFpConfirm(e.target.value)} data-testid="forgot-confirmpass" /></div>
                        <Button onClick={fpReset} disabled={fpLoading} className="w-full pill-primary rounded-full h-11" data-testid="forgot-reset">{fpLoading ? tr("fp_resetting") : fpMode === "set" ? tr("id_setpw_submit") : tr("fp_reset")}</Button>
                      </div>
                    )}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="register">
                {ageCheck === "checking" || ageCheck === "blocked" ? (
                  <div className="mt-6 flex items-center justify-center gap-2 py-10 text-sm" style={{ color: "var(--yalola-text-muted)" }} data-testid="register-age-check">
                    <ShieldCheck size={18} style={{ color: "var(--yalola-primary)" }} />
                    <span>{t("onboarding.age_check", "Vérification de la tranche d'âge…")}</span>
                  </div>
                ) : stats?.is_full ? (
                  <div className="mt-6 text-center py-8 px-4" data-testid="register-full">
                    <div className="h-16 w-16 mx-auto rounded-full grid place-items-center" style={{ background: "var(--yalola-primary-soft)" }}>
                      <Users size={28} style={{ color: "var(--yalola-primary)" }} />
                    </div>
                    <h3 className="mt-4 text-xl font-bold font-display" style={{ color: "var(--yalola-text)" }}>{tr("full_title")}</h3>
                    <p className="mt-2 text-sm leading-relaxed max-w-sm mx-auto" style={{ color: "var(--yalola-text-muted)" }}>{tr("full_desc")}</p>
                    <Button variant="outline" className="rounded-full mt-5" onClick={() => setTab("login")} data-testid="register-full-login">{tr("login")}</Button>
                  </div>
                ) : regView === "otp" ? (
                  <div className="mt-4 space-y-4" data-testid="register-otp">
                    <div className="text-center">
                      <div className="h-16 w-16 mx-auto rounded-full grid place-items-center" style={{ background: "var(--yalola-primary-soft)" }}>
                        {regOtpChannel === "whatsapp" ? <MessageCircle size={30} style={{ color: "var(--yalola-primary)" }} /> : <MailCheck size={30} style={{ color: "var(--yalola-primary)" }} />}
                      </div>
                      <h3 className="mt-3 text-xl font-bold font-display">{regOtpChannel === "whatsapp" ? tr("wa_otp_title") : tr("otp_title")}</h3>
                      <p className="mt-1 text-sm" style={{ color: "var(--yalola-text-muted)" }}>{regOtpChannel === "whatsapp" ? tr("wa_otp_desc") : tr("otp_desc")} <b style={{ color: "var(--yalola-text)" }}>{regOtpChannel === "whatsapp" ? otpPhone : otpEmail}</b></p>
                    </div>
                    <Input inputMode="numeric" maxLength={6} value={otpCode} onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ""))} placeholder={tr("otp_placeholder")} className="text-center text-3xl tracking-[0.5em] font-bold h-16" data-testid="otp-input" />
                    <Button onClick={verifyOtp} disabled={otpLoading} className="w-full pill-primary rounded-full h-11" data-testid="otp-verify">{otpLoading ? tr("otp_verifying") : tr("otp_verify")}</Button>
                    <div className="flex items-center justify-between">
                      <button type="button" onClick={() => { setRegView("form"); setOtpCode(""); }} className="text-sm" style={{ color: "var(--yalola-text-muted)" }} data-testid="otp-change-email">{regOtpChannel === "whatsapp" ? tr("wa_change_number") : tr("otp_change")}</button>
                      <button type="button" onClick={resendOtp} disabled={cooldown > 0} className="text-sm font-medium" style={{ color: cooldown > 0 ? "var(--yalola-text-muted)" : "var(--yalola-primary)" }} data-testid="otp-resend">{cooldown > 0 ? tr("otp_resend_in").replace("{s}", cooldown) : tr("otp_resend")}</button>
                    </div>
                    {regOtpChannel !== "whatsapp" && <p className="text-[11px] text-center" style={{ color: "var(--yalola-text-muted)" }}>{tr("otp_check_spam")}</p>}
                  </div>
                ) : (
                <div className="mt-4" data-testid="register-wizard" data-unified={unifiedLogin ? "1" : "0"}>
                  {!unifiedLogin && waAuthEnabled && (
                  <div className="grid grid-cols-2 gap-2 p-1 rounded-full mb-3" style={{ background: "var(--yalola-surface-alt)" }} data-testid="register-method-toggle">
                    {registerMethodOrder.map((m) => { const [label, Icon] = methodMeta[m]; return (
                      <button key={m} type="button" onClick={() => { setRegMethod(m); setWaUnavailable(false); }} className="flex items-center justify-center gap-1.5 h-9 rounded-full text-sm font-semibold transition-colors" style={{ background: regMethod === m ? "white" : "transparent", color: regMethod === m ? "var(--yalola-primary)" : "var(--yalola-text-muted)", boxShadow: regMethod === m ? "0 1px 3px rgba(0,0,0,0.08)" : "none" }} data-testid={`register-method-${m}`}>
                        <Icon size={15} /> {label}
                      </button>
                    ); })}
                  </div>
                  )}
                  {waUnavailable && regMethodEff === "whatsapp" && (
                    <div className="mb-3 rounded-xl p-3 text-sm" style={{ background: "rgba(220,38,38,0.08)", border: "1px solid rgba(220,38,38,0.25)" }} data-testid="wa-unavailable-notice">
                      <p className="mb-2" style={{ color: "var(--yalola-text)" }}>{tr("wa_unavailable")}</p>
                      <button type="button" onClick={() => { setRegMethod("email"); setRegIdentifier(""); setRegLocked(false); setWaUnavailable(false); setStep(1); }} className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-sm font-semibold text-white" style={{ background: "var(--yalola-primary)" }} data-testid="continue-with-email-btn">
                        <Mail size={15} /> {tr("wa_continue_email")}
                      </button>
                    </div>
                  )}
                  <StepDots />
                  {draftRestored && regView === "form" && (
                    <div className="mb-3 flex items-center justify-between gap-2 rounded-xl px-3 py-2 text-xs" style={{ background: "var(--yalola-surface-alt)", color: "var(--yalola-text-muted)" }} data-testid="reg-draft-notice" data-needs-password={draftNeedsPassword ? "1" : "0"}>
                      <span className="flex items-center gap-1.5 min-w-0"><Info size={13} className="shrink-0" style={{ color: "var(--yalola-primary)" }} aria-hidden /><span>{draftNeedsPassword ? tr("reg_draft_restored_pw") : tr("reg_draft_restored")}</span></span>
                      <button type="button" onClick={resetRegisterDraft} className="shrink-0 font-semibold underline" style={{ color: "var(--yalola-primary)" }} data-testid="reg-draft-reset">{tr("reg_draft_reset")}</button>
                    </div>
                  )}
                  <div className="text-center font-bold font-display mb-3" data-testid="reg-step-title">
                    {step === 1 ? tr("step1") : step === 2 ? tr("step2") : tr("step3")}
                    {lightSignup && <div className="text-xs font-normal mt-0.5" style={{ color: "var(--yalola-text-muted)" }} data-testid="reg-light-hint">{tr("light_hint")}</div>}
                  </div>

                  {step === 1 && (
                    <div className="space-y-3 lg:max-h-[48vh] lg:overflow-y-auto lg:pr-1" data-testid="reg-step-1">
                      <div className="grid grid-cols-2 gap-3">
                        <div><Label>{tr("first_name")} *</Label><Input required value={regData.first_name} onChange={(e) => set("first_name", e.target.value)} data-testid="reg-firstname" /></div>
                        <div><Label>{tr("last_name")} *</Label><Input required value={regData.last_name} onChange={(e) => set("last_name", e.target.value)} data-testid="reg-lastname" /></div>
                      </div>
                      {unifiedLogin ? (
                        <div className="space-y-3" data-testid="reg-identifier-wrap" data-kind={regKind || "none"} data-locked={regLocked ? "1" : "0"}>
                          {regLocked ? (
                            <div>
                              <Label>{tr("id_label")} *</Label>
                              <div className="mt-1 flex items-center gap-3 rounded-2xl border p-3" style={{ borderColor: "var(--yalola-border)", background: "var(--yalola-surface)" }} data-testid="reg-identifier-locked">
                                <div className="h-9 w-9 rounded-full grid place-items-center shrink-0" style={regKind === "phone" ? { background: "#DCFCE7", color: "#15803D" } : { background: "#EFF6FF", color: "#1D4ED8" }}>
                                  {regKind === "phone" ? <MessageCircle size={18} /> : <Mail size={18} />}
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="text-sm font-semibold break-all" data-testid="reg-identifier-locked-value">
                                    {regKind === "phone" && regPhoneEff.iso ? `${flagEmoji(regPhoneEff.iso)} ${regPhoneEff.e164 || regIdentifier}` : regIdentifier}
                                  </div>
                                  <div className="text-xs" style={{ color: "var(--yalola-text-muted)" }} data-testid="reg-identifier-locked-hint">{regKind === "phone" ? tr("wa_reg_hint") : tr("reg_locked_hint")}</div>
                                </div>
                                <button type="button" onClick={editLockedIdentifier} className="text-xs font-semibold shrink-0" style={{ color: "var(--yalola-primary)" }} data-testid="reg-identifier-edit">{tr("reg_id_edit")}</button>
                              </div>
                            </div>
                          ) : (
                            <div>
                              <Label>{tr("id_label")} *</Label>
                              <IdentifierField
                                value={regIdentifier} iso={regIdentifierIso || detectedIso || regData.country || "CD"} onChange={setRegIdentifier} onIsoChange={setRegIdentifierIso} lang={lang}
                                labels={{ placeholder: tr("id_placeholder"), hint: regWaOff ? "" : regKind === "phone" ? tr("wa_reg_hint") : regKind === "email" ? tr("reg_id_hint_email") : tr("id_hint"), email: tr("id_tag_email"), whatsapp: tr("id_tag_wa") }}
                                testId="reg-identifier" onCheck={setRegCheck}
                              />
                              {regWaOff && (
                                <p className="mt-1.5 text-[11px] leading-snug flex items-start gap-1" style={{ color: "#B91C1C" }} data-testid="reg-wa-off-notice">
                                  <ShieldAlert size={12} className="shrink-0 mt-0.5" /> <span>{tr("reg_wa_off")}</span>
                                </p>
                              )}
                            </div>
                          )}
                          {regKind === "email" && (
                            <div><Label>{tr("password")} * {tr("password_hint")}</Label><PasswordInput required minLength={6} value={regData.password} onChange={(e) => set("password", e.target.value)} data-testid="reg-password" /><PasswordStrength value={regData.password} lang={lang} /></div>
                          )}
                        </div>
                      ) : waAuthEnabled && regMethod === "whatsapp" ? (
                        <div data-testid="reg-wa-phone-wrap">
                          <Label>{tr("wa_phone_label")} *</Label>
                          <PhoneField value={regPhone} onChange={setRegPhone} lang={lang} testId="reg-phone" defaultIso={detectedIso || regData.country || "CD"} onCheck={setRegCheck} />
                          <p className="text-[11px] mt-1" style={{ color: "var(--yalola-text-muted)" }}>{tr("wa_reg_hint")}</p>
                        </div>
                      ) : (
                        <div className="grid grid-cols-2 gap-3">
                          <div><Label>{tr("email")} *</Label><Input type="email" required value={regData.email} onChange={(e) => set("email", e.target.value)} data-testid="reg-email" /></div>
                          <div><Label>{tr("password")} * {tr("password_hint")}</Label><PasswordInput required minLength={6} value={regData.password} onChange={(e) => set("password", e.target.value)} data-testid="reg-password" /><PasswordStrength value={regData.password} lang={lang} /></div>
                        </div>
                      )}
                      <div className="grid grid-cols-2 gap-3" data-testid={lightSignup ? "reg-light-row" : undefined}>
                        {lightSignup ? (
                          <div className="min-w-0">
                            <Label>{tr("denomination")} *</Label>
                            <Select value={regData.denomination || undefined} onValueChange={(v) => set("denomination", v)}>
                              <SelectTrigger data-testid="reg-denomination"><SelectValue placeholder={tr("choose")} /></SelectTrigger>
                              <SelectContent className="max-h-64">{denoms.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                            </Select>
                          </div>
                        ) : (
                        <div>
                          <Label>{tr("gender")}{req("gender")}</Label>
                          <Select value={regData.gender} onValueChange={(v) => set("gender", v)}>
                            <SelectTrigger data-testid="reg-gender"><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="male">{tr("male")}</SelectItem>
                              <SelectItem value="female">{tr("female")}</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        )}
                        <div className="min-w-0"><Label>{tr("birth")}{(req("birth_date") || (stats?.min_age > 0 || stats?.max_age > 0)) ? <span style={{ color: "var(--yalola-primary)" }}> *</span> : null}</Label><BirthDatePicker placeholder={tr("birth_ph")} lang={lang} minAge={stats?.min_age || 0} maxAge={stats?.max_age || 0} value={regData.birth_date} onChange={(v) => set("birth_date", v)} data-testid="reg-birthdate" />{stats?.min_age > 0 && <div className="text-[11px] mt-1" style={{ color: "var(--yalola-text-muted)" }} data-testid="reg-age-hint">{tr("age_hint_min").replace("{min}", stats.min_age)}</div>}</div>
                      </div>
                      {/* Lot 705 — code d'invitation : pré-rempli par le lien (?ref=) ou le code mémorisé, modifiable (code lu sur une capture). */}
                      <div data-testid="reg-referral-row">
                        <Label className="text-xs">{tr("ref_code_label")}</Label>
                        <Input value={refCode} onChange={(e) => { setRefCode(normalizeRefCode(e.target.value) || e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, "").slice(0, 12)); setRefFromLink(false); }}
                          placeholder={tr("ref_code_ph")} maxLength={12} autoCapitalize="characters" autoComplete="off" className="mt-1 h-10 uppercase tracking-widest font-semibold" data-testid="reg-referral-code" />
                        <div className="text-[11px] mt-1" style={{ color: refFromLink && refCode ? "#1B7A3D" : "var(--yalola-text-muted)" }} data-testid="reg-referral-hint">
                          {refFromLink && refCode ? `✓ ${tr("ref_code_from_link")}` : tr("ref_code_hint")}
                        </div>
                        {/* Lot 707 — bonus de bienvenue (interrupteur admin) : donne envie de s'inscrire avec le code */}
                        {config?.referral?.points_enabled && config?.referral?.welcome_bonus_enabled && (
                          <div className="text-[11px] mt-0.5 font-semibold" style={{ color: "var(--yalola-primary)" }} data-testid="reg-referral-bonus">
                            🎁 {tr("ref_code_bonus").replace("{points}", config.referral.welcome_bonus_points)}
                          </div>
                        )}
                      </div>
                      {!consentDone && <label className="flex items-start gap-2.5 mt-1 cursor-pointer" data-testid="reg-accept-terms-row">
                        <Checkbox checked={acceptTerms} onCheckedChange={(v) => setAcceptTerms(!!v)} className="mt-0.5" data-testid="reg-accept-terms" />
                        <span className="text-xs leading-relaxed" style={{ color: "var(--yalola-text-muted)" }}>
                          {tr("accept_prefix")}{" "}
                          <a href={withLang("/legal/terms", lang)} target="_blank" rel="noreferrer" className="font-semibold underline inline-block py-1 px-0.5 -my-1" style={{ color: "var(--yalola-primary)" }} data-testid="reg-terms-link">{tr("legal_terms")}</a>{" "}
                          {tr("accept_and")}{" "}
                          <a href="/legal/privacy" target="_blank" rel="noreferrer" className="font-semibold underline inline-block py-1 px-0.5 -my-1" style={{ color: "var(--yalola-primary)" }} data-testid="reg-privacy-link">{tr("legal_privacy_full")}</a>.
                        </span>
                      </label>}
                      <p className={`flex items-start gap-1.5 text-[11px] leading-relaxed ${consentDone ? "mt-1" : "pl-7"}`} style={{ color: "var(--yalola-text-muted)" }} data-testid="reg-zero-tolerance-hint">
                        <ShieldCheck size={13} className="shrink-0 mt-[1px]" style={{ color: "var(--yalola-primary)" }} aria-hidden />
                        <span>{tr("zero_tolerance_hint")} {legalLink("/legal/terms#zero-tolerance", tr("legal_terms"), "reg-zero-tolerance-link")}</span>
                      </p>
                    </div>
                  )}

                  {step === 2 && (
                    <div className="space-y-3 lg:max-h-[48vh] lg:overflow-y-auto lg:pr-1" data-testid="reg-step-2">
                      {/* "Vous êtes..." lives here: it relates to church & ministry */}
                      <div>
                        <Label>{tr("you_are")}</Label>
                        <div className="grid grid-cols-3 gap-2 mt-1">
                          {ROLES_CHOICE.map(({ key, label, icon: Icon }) => (
                            <button
                              key={key}
                              type="button"
                              onClick={() => set("role", key)}
                              className="p-2 rounded-xl border text-xs font-medium flex flex-col items-center gap-1"
                              style={{
                                borderColor: regData.role === key ? "var(--yalola-primary)" : "var(--yalola-border)",
                                background: regData.role === key ? "var(--yalola-surface-alt)" : "white",
                                color: regData.role === key ? "var(--yalola-primary)" : "var(--yalola-text)",
                              }}
                              data-testid={`reg-role-${key}`}
                            >
                              <Icon size={16} />
                              <span className="text-center leading-tight">{label}</span>
                            </button>
                          ))}
                        </div>
                      </div>

                      {["preacher", "artist", "influencer"].includes(regData.role) && (
                        <p className="text-[11px] rounded-lg px-3 py-2" style={{ background: "var(--yalola-primary-soft)", color: "var(--yalola-text-muted)" }} data-testid="reg-role-kyc-hint">
                          {tr("role_verify_hint")}
                        </p>
                      )}

                      <div>
                        <Label>{tr("denomination")}{req("denomination")}</Label>
                        <Select value={regData.denomination || undefined} onValueChange={(v) => set("denomination", v)}>
                          <SelectTrigger data-testid="reg-denomination"><SelectValue placeholder={tr("choose")} /></SelectTrigger>
                          <SelectContent className="max-h-64">{denoms.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label>{tr("country")}{req("country")}</Label>
                          <Select value={regData.country || undefined} onValueChange={(v) => set("country", v)}>
                            <SelectTrigger data-testid="reg-country"><SelectValue placeholder={tr("choose")} /></SelectTrigger>
                            <SelectContent className="max-h-64">
                              {countries.map((c) => <SelectItem key={c.code} value={c.code}>{c.label}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </div>
                        <div><Label>{tr("city")}{req("city")}</Label><CityAutocomplete value={regData.city} country={regData.country} onChange={(v) => set("city", v)} testId="reg-city" /></div>
                      </div>
                      {/* Lot 659 : pays + ville en un tap depuis la position du téléphone */}
                      <NearestCity onPick={(p) => { set("country", p.country); set("city", p.city); }} testId="reg-nearest" />
                    </div>
                  )}

                  {step === 3 && (
                    <div data-testid="reg-step-3">
                      <p className="text-sm mb-2" style={{ color: "var(--yalola-text-muted)" }}>
                        {tr("interests_hint")}{!reqOf("interests") ? ` (${tr("optional")})` : ""} — {regData.interests.length}/3
                      </p>
                      <div className="flex flex-wrap gap-1.5" data-testid="reg-interests">
                        {interestOptions(config, lang).map(({ key: i, label }) => (
                          <button
                            key={i}
                            type="button"
                            onClick={() => toggleInterest(i)}
                            className={`chip ${regData.interests.includes(i) ? "!bg-[color:var(--yalola-primary)] !text-white !border-transparent" : ""}`}
                            data-testid={`reg-interest-${i}`}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="flex gap-2 mt-5">
                    {step > 1 && (
                      <Button variant="outline" className="rounded-full flex-1" onClick={() => setStep((x) => x - 1)} disabled={loading} data-testid="reg-back">
                        <ChevronLeft size={16} className="mr-1" /> {tr("back")}
                      </Button>
                    )}
                    {step < lastStep ? (
                      <Button className="pill-primary rounded-full flex-1 h-11" onClick={nextStep} disabled={step === 1 && regMethodEff === "whatsapp" && (regWaOff || isBlockingCheck(regCheck))} data-testid="reg-next">
                        {tr("next")} <ChevronRight size={16} className="ml-1" />
                      </Button>
                    ) : (
                      <Button className="pill-primary rounded-full flex-1 h-11" onClick={doRegister} disabled={loading || (lightSignup && regMethodEff === "whatsapp" && (regWaOff || isBlockingCheck(regCheck)))} data-testid="reg-submit">
                        {loading ? tr("creating") : at("register_button", tr("register"))}
                      </Button>
                    )}
                  </div>
                </div>
                )}
              </TabsContent>
            </Tabs>
          </div>
        </div>
      </div>

      <footer className="relative text-center pb-6 text-xs space-y-1.5" style={{ color: "var(--yalola-text-muted)" }} data-testid="onboarding-footer">
        {footerText ? <div data-testid="footer-copyright">{footerText}</div> : null}
        <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
          {(config?.settings?.features?.public_discover) !== false && (<>
            <Link to="/decouvrir" className="hover:underline" data-testid="footer-discover-link">{tr("discover")}</Link>
            <span aria-hidden>·</span>
          </>)}
          <Link to="/legal/terms" className="hover:underline" data-testid="footer-legal-terms">{tr("legal_terms")}</Link>
          <span aria-hidden>·</span>
          <Link to="/legal/privacy" className="hover:underline" data-testid="footer-legal-privacy">{tr("legal_privacy")}</Link>
          <span aria-hidden>·</span>
          <Link to="/legal/gdpr" className="hover:underline" data-testid="footer-legal-gdpr">{tr("legal_gdpr")}</Link>
          <span aria-hidden>·</span>
          <Link to="/legal/child-safety" className="hover:underline" data-testid="footer-legal-child-safety">{tr("legal_child")}</Link>
          <span aria-hidden>·</span>
          <Link to="/supprimer-mon-compte" className="hover:underline" data-testid="footer-legal-delete">{tr("legal_delete")}</Link>
        </div>
      </footer>

      {/* Encourage phone/tablet visitors to install the PWA (auto-opens once). */}
      <InstallAppButton variant="popup" />
    </div>
  );
};

export default Onboarding;
